import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, Injector, ViewChild } from '@angular/core';
import { lockClick } from 'directives/cs-capture-cover/cs-capture-cover.directive';
import { LL } from 'i18n/i18n.service';
import { TranslatedString, untranslatedString } from 'models/translated-string';
import { CsPage, NavController } from 'providers/nav-controller';
import { confirmDiscardAsync } from 'providers/unsaved-changes-guard/unsaved-changes-guard';
import { waitForAngularUpdate } from 'services/browser-utils';
import { isE2ETest } from 'environments/environment-helpers';
import { CsInputMeasureComponent } from 'components/cs-input-measure/cs-input-measure';
import { ToastController } from 'providers/toast-controller';
import { ToastType } from 'components/toast/toast-host';
import { CannotTuneReason, classifyCannotTune } from 'util/cannot-tune-reason';
import { SettingsService } from 'services/settings.service';
import { PageRef } from 'providers/page-ref';
import { getPageName } from 'app/app-page-helpers';
import { FoodDetailsResult, IEditFoodPage } from 'pages/edit-food/edit-food-interface';
import { ModalPageMarker } from 'providers/modal-page-marker';
import { FoodDbService, MealTunerSource } from 'services/food-db.service';
import { MealTuner } from '@cf-shared/meal-tuner';
import { MealTunerOutcome } from '@cf-shared/meal-tuner-interface';
import { UserDb } from 'services/user-db.service';
import { PopupMenuButton, PopupMenuProvider } from 'providers/popup-menu/popup-menu';
import { AlertButtonRole, AlertController } from 'providers/alert-controller';
import { Log_Meal } from '@cf-shared/data-model/log-meal';
import { mealTargetsAsIfEmpty, tunableTargetsOr } from '@cf-shared/meal-targets-as-if-empty';
import { Log_DayStart } from '@cf-shared/data-model/log-day-start';
import { EditedMealRef, sumDayNutrientsWithEditedMeal } from './day-nutrient-total';
import { LogRecordState, LogRecordType } from '@cf-shared/data-model/user-log';
import { DateTime } from 'luxon';
import { DatabaseWrapper, DayUpdatedEvent, DocWatcher } from 'services/database-wrapper';
import { PubSubService } from 'services/pubsub.service';
import { DebugService } from 'services/debug.service';
import { LoggingService } from 'services/logging.service';
import { buildRefuseHint } from './refuse-hint';
import { dropClearedNutrientInputs } from './cleared-nutrient-inputs';
import {
  buildWeighedWithLabel,
  buildWeighedWithNote,
  eatenWeight,
  readWeighedWithRefuse,
  typedWeight,
  usableRefusePercent,
} from './weighed-with-refuse';
import { tunedRowsStillMatchMeal } from './tuned-existing-rows';
import { AuthService } from 'services/auth.service';
import { TelemetryService } from 'services/telemetry.service';
import { FoodReadFailedError, FoodReadFailurePolicy } from 'services/food-read-error';
import { isDatabaseUnavailable } from 'services/database-unavailable-error';
import { showFoodLoadFailedAlertAsync } from 'services/food-load-failed-alert';
import {
  addIngredientIntoMealTotal,
  addNutrientsInto,
  Ingredient,
  IngredientReal,
  IngredientType,
  isIngredientMacro,
  isIngredientReal,
  isUsableIngredientWeight,
  mergeUnusableNutrientInput,
  newUnusableNutrientInput,
  nutrientsForWeightForDisplay,
} from '@cf-shared/data-model/ingredient';
import { describeIngredientForReport, reportUnusableNutrientInput } from 'util/nutrient-data-quality-reporter';
import {
  ComplexFood,
  Food,
  FoodMeasure,
  FoodStatus,
  foodStatusIcons,
  INFOODS,
  isComplexFood,
  isComplexFoodById,
  Nutrients,
  NutrientTargets,
  getFoodReferenceWeight,
  getGrossMismatchExpectedCalories,
  isNutrientValueEntered,
  checkNutrientCalories,
} from '@cf-shared/data-model/food';
import { isRecipe, calculateRecipeTotalWeight } from '@cf-shared/data-model/recipe';
import { hasSplitParts } from '@cf-shared/data-model/split-recipe-portion';
import { MeasureUnitConfig } from '@cf-shared/measure-configs';
import { NutrientDefinitionsOrdered } from '@cf-shared/data-model/display-nutrient';
import { scaleNutrientsToPer100g } from 'util/nutrient-scaling';
import { UiDisplayNutrient } from 'models/display-nutrient';
import { FormatNutrients, formatScheduledMealName, formatServingSize, translateMeasureName, isBuiltInMeasureName } from 'util/formatting';
import { NutrientBarStyle, NutrientDisplayMode } from 'components/nutrient-list/nutrient-list';
import { browserLocalStorageOrNull } from 'environments/environment-helpers';
import { RoundToDecimals } from '@cf-shared/shared-util';
import { deepClone, deepCompareEQ } from '@cf-shared/deep-helpers';
import { ingredientsToRepeat } from 'components/ingredient-search/recent-meals-layout';
import { markerAfterAdding } from 'util/meal-source-marker';
import { describeErrorForLog } from '@cf-shared/error-describe';
import { buildFoodAddedToastMessage, FOOD_ADDED_TOAST_DURATION_MS, FOOD_ADDED_TOAST_PRESENTATION } from 'util/food-added-toast';

import { EditIngredientMode, EditIngredientPageParams, EditIngredientResult, EditIngredientReturnCapability } from './edit-ingredient-interface';

/**
 * What the macro card on this page is describing.
 *
 * A third position, `Day`, is planned and deliberately absent: it needs the
 * day's own nutrients and targets, which this page is never handed. The dial is
 * built from a list so adding it later is one more entry, not a redesign.
 */
enum MacroCardScope {
  /** Just this food, at the amount currently typed. */
  Food = 'food',
  /** The whole meal, with this food's amount included. */
  Meal = 'meal',
  /** The whole day, with this food's amount included. */
  Day = 'day',
}

/**
 * Why logging the food into the day's meal did not go ahead, leaving this page on screen.
 *
 * `saveToMealAndNavigateAsync` answers with one of these instead of handling it, so that
 * `handleSave` -- the one method that arms `isExiting` -- is also the one that clears it and
 * shows the matching alert (df-998ad478267d).
 */
enum SaveToMealRefusal {
  /** The day has no meal to log into. */
  NoMealOnDay = 'no-meal-on-day',
  /** A food could not be read, so the meal total would have been too low. The meal has
   * already been put back exactly as it was. */
  FoodCouldNotBeRead = 'food-could-not-be-read',
  /** The past meal being logged holds no food that can be logged, so nothing was written (#6188). */
  PastMealHasNoUsableFoods = 'past-meal-has-no-usable-foods',
}

/**
 * Where the two dial positions are remembered between visits. Per device and
 * not synced -- which dial you last looked at is a habit, not account data.
 *
 * Neither key is on the sign-out allow-list in `local-storage-hygiene.ts`, and
 * that is what makes them user-scoped: everything not on that list is wiped
 * when an account signs out, so the next account starts fresh with no code here
 * to remember to write. See design-docs/local-storage-and-auto-plan-cache.md.
 */
const MACRO_SCOPE_STORAGE_KEY = 'cs_portion_macro_scope';
const MACRO_MODE_STORAGE_KEY = 'cs_portion_macro_mode';

/**
 * Does this set of nutrient targets actually hold any?
 *
 * The one home for a rule this screen needs in several places: a target map is a plain
 * object, so an EMPTY one is truthy while holding nothing. A meal the user added outside
 * their plan carries exactly that. Testing it for truth therefore answers "yes, there are
 * targets" for a meal that has none, and every reading built on top of that answer is
 * wrong in its own way -- "Left" would subtract nothing from each value and then label
 * what the user just ate as what they have left to eat, and the meal tuner would be handed
 * an empty map to aim at and crash on it (deferred finding df-fc1cd793ae3b).
 *
 * Counting the keys is what tells the two apart, and an absent map answers no by the same
 * test rather than needing one of its own.
 */
function holdsAnyTargets(targets: NutrientTargets | undefined): boolean {
  return !!targets && Object.keys(targets).length > 0;
}

@Component({
  selector: 'page-edit-ingredient',
  templateUrl: 'edit-ingredient.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
  providers: [ModalPageMarker]
})
export class EditIngredientPage extends CsPage<EditIngredientPageParams> implements OnDestroy {
  protected LL = LL;
  NutrientDisplayMode = NutrientDisplayMode;
  protected ingredient: Ingredient;
  protected ingredientIndex: number;
  protected ingredients: Ingredient[];
  protected targets: NutrientTargets;
  protected mode: EditIngredientMode;
  protected food: Food;

  protected customIngredientUnits: MeasureUnitConfig[] = [];
  protected weight_unit: string;
  protected custom_unit: string;
  protected isComplexFood = false;
  /**
   * True when the complex food loaded on this screen has at least one part to split into. A
   * recipe saved with no ingredients splits into nothing, so its Split controls could never do
   * anything (df-5e2bcf38fbf2). Not used for a log meal being copied, which has its own rule.
   */
  private complexFoodHasParts = false;
  /** macro mode only: the typed-in calories disagree with the typed-in macros */
  protected errorMessage: TranslatedString;
  /**
   * Set when the food behind a real ingredient could not be shown: either it could not be
   * read just now, or it no longer exists. The two get different wording on purpose -- telling
   * somebody their food is gone when their connection dropped sends them off to re-create a
   * food they still have.
   *
   * A field of its own, drawn in the real-ingredient section of the template. It used to share
   * errorMessage, which the template draws only for a macro ingredient, so the warning was set
   * and never seen (df-6243048bcd73).
   */
  protected foodLoadProblem: TranslatedString | undefined;
  /**
   * Set when the food being logged states calories its own macros contradict (#4541).
   *
   * Kept apart from foodLoadProblem, which reports that the food could not be loaded at all --
   * one is a warning about the data, the other is a failure of this screen, and letting
   * either overwrite the other would hide a real error behind a data warning.
   */
  protected inconsistentNutrientsWarning: TranslatedString;
  /** "weigh it without the shell" under the amount, for a food whose document says what is not eaten (food staples plan 4a) */
  protected refuseHint: TranslatedString | undefined;
  /** The "Weighed with peel / pit / bone" checkbox (#5544). The label is set only when the box is
   * offered: the food has a usable uneaten share, or the row was already ticked with one. */
  protected weighedWithLabel: TranslatedString | undefined;
  /** whether the box is ticked; when it is, `ingredient.weighedWithRefuse` holds the percent used */
  protected isWeighedWithRefuseTicked = false;
  /** the weight as typed, with the uneaten part, shown in the grams field while the box is ticked */
  protected typedWeight_g: number;
  /** "Counted as 118 g eaten: 36% of the weight is taken off.", while the box is ticked */
  protected weighedWithNote: TranslatedString | undefined;
  /** the share the box takes off when ticked: the row's own stored percent, else the food's */
  private weighedWithPercent: number | undefined;
  /** The weights at the moment the box was last unticked. Ticking again with the same number still in
   * the field restores the exact eaten weight, because working it out again (typed x (1 - p) after
   * typed = eaten / (1 - p)) can come back one rounding step off, which would read as an edit. */
  private weightsAtUntick: { typed_g: number; eaten_g: number } | undefined;

  /**
   * What the caller that opened this screen can apply from its answer, from
   * `EditIngredientPageParams.returnCapability`. Read once in `ngOnInit`, because the split
   * controls are drawn from it and a value that changed underneath them would show a control
   * whose answer nobody can use.
   */
  private returnCapability = EditIngredientReturnCapability.SplitOrSingleIngredient;

  /**
   * True when a split this screen produced would actually reach the caller.
   *
   * A split pops this page with a whole LIST of ingredients, so a caller that can hold only one
   * has nowhere to put it and drops the answer. Offering the control anyway is what deferred
   * finding df-d3f1a6b577ee was: the meal-photo scanner's Split buttons did nothing at all.
   */
  private get callerCanApplySplit(): boolean {
    return this.returnCapability !== EditIngredientReturnCapability.SingleIngredientOnly;
  }

  // True when the food can be split into individual ingredients (recipes or log meals), AND the
  // caller that opened this screen can apply the split it would hand back. A recipe must also
  // have at least one part: an empty one splits into nothing, and offering Split for it gave a
  // button that did nothing however often it was pressed (df-5e2bcf38fbf2). A log meal being
  // copied keeps its own rule.
  get canSplitFood(): boolean {
    if (!this.callerCanApplySplit) return false;
    if (this.sourceLogMeal) return true;
    return this.isComplexFood && this.complexFoodHasParts;
  }

  /**
   * True when a split on this screen has something to TUNE the amounts towards.
   *
   * The tuner aims at the meal's plan targets, so with no targets there is nothing to aim at
   * and the split can only ever come back at the weights it went in with. Offering "Split &
   * Auto-Tune" anyway is deferred finding df-fc1cd793ae3b: the foods were split, the amounts
   * were left exactly as they were, and nothing said so.
   *
   * Asked through `holdsAnyTargets`, which is where the "an empty map is truthy" rule lives:
   * every guard further down this path tests truthiness, so an empty map reaches the tuner as
   * if it were a real set of targets and crashes it.
   *
   * The meal editor asks the same question before offering its own Auto-Tune -- see
   * `canAutoTune` in edit-meal.ts, which refuses when the meal has no plan targets.
   */
  protected get canAutoTuneSplit(): boolean {
    return holdsAnyTargets(this.targets);
  }

  /**
   * Whether the amount the user typed cannot be saved: the field is empty, or holds text that
   * is not a number, so the weight it binds to is `undefined` (df-6aedbb363d8c). Saving then
   * would put the food into the meal with no weight, where it counts for nothing.
   *
   * Asked only where the amount fields are shown -- a real food not taken from a past meal --
   * so a typed-in ingredient or a past meal's foods save exactly as they always did. While
   * "Weighed with peel" is ticked the field holds the typed weight, so that is checked too.
   */
  protected get hasUnusableAmount(): boolean {
    if (!isIngredientReal(this.ingredient) || this.sourceLogMeal) return false;
    if (!isUsableIngredientWeight(this.ingredient.weight_g)) return true;
    return this.isWeighedWithRefuseTicked && !isUsableIngredientWeight(this.typedWeight_g);
  }

  /**
   * What "Split & Auto-Tune" tunes the meal towards: what it should CONTAIN given how the rest of
   * its day went, not the display figure `this.targets` (df-b16896c0bd79; why the two differ:
   * `design-docs/meal-target-figures.md`). Built from the day already read for the Day scope.
   * With no day entry behind the screen, the meal's own plan figure is used, as before.
   */
  private get splitTuningTargets(): NutrientTargets {
    const meal = this.selectedLogMeal
      ?? (this.editedMeal ? { ...this.editedMeal, targets: this.targets } : undefined);
    if (!meal || !this.logDay || !this.dayDate) return this.targets;

    const asIfEmpty = mealTargetsAsIfEmpty(meal, this.logMeals, this.logDay.targets || {}, DateTime.fromISO(this.dayDate));
    // if the rest of the day already used up its calories, the figure carries no calorie target and
    // the tuner refuses it as a crash -- so tune against the meal's own plan figure instead. an
    // over-eaten day is not a fault to report. `tunableTargetsOr` is the one place that rule lives,
    // and it mirrors the tuner's own refusal rule.
    return tunableTargetsOr(asIfEmpty, this.targets);
  }

  protected displayNutrients: UiDisplayNutrient[];
  protected displayNutrientsCustom: UiDisplayNutrient[];
  protected totalNutrients: Nutrients = {};
  protected targetDisplayNutrients: UiDisplayNutrient[];
  /** The whole day's nutrients, this food included, against the day's own targets. */
  protected dayDisplayNutrients: UiDisplayNutrient[];
  private calculationId = 0;
  /**
   * True when the running total in `totalNutrients` could NOT be added up in full,
   * because a food this meal already holds could not be READ this time.
   *
   * While it is set, neither wider reading is drawn: both are short by whatever that
   * ingredient held, and a total that is quietly too low is worse than no total, because
   * the user acts on it. The next recalculation that reads every food clears it.
   */
  private mealTotalIncomplete = false;
  /** Whether each of the two conditions below has already been reported on this visit. */
  private reportedMealTotalReadFailure = false;
  private reportedMealTotalMissingFood = false;

  protected foodStatusIcons = foodStatusIcons;
  protected IngredientType = IngredientType;
  protected EditIngredientMode = EditIngredientMode;
  protected isIngredientReal = isIngredientReal;
  protected isIngredientMacro = isIngredientMacro;

  // reactive sync
  private docWatcher: DocWatcher;

  // The day this screen sits on. Log mode works it out from its own nav params; the
  // New and Edit paths are handed it by the meal page. Absent where there is no day.
  protected dayDate: string;
  protected logMeals: Log_Meal[] = [];
  /** The day's own log entry, holding the day's targets. */
  protected logDay: Log_DayStart;

  /**
   * The meals the "Meal" picker offers: every meal on the day EXCEPT the ones the
   * user marked as skipped.
   *
   * A skipped meal is food nobody ate, so `updateDayValuesInMemory` in
   * `user-db.service.ts` leaves it out of the day's totals. Logging a food into one
   * therefore stored the food on the meal and moved no day total at all, with
   * nothing on screen saying why.
   *
   * `logMeals` itself stays the day's FULL list, skipped meals and duplicates
   * included, because `updateDayDisplayNutrients` sums the day from it and needs
   * both to get that sum right. Written as a getter rather than a second field so
   * the two lists cannot drift apart when one of `logMeals`'s assignments is
   * missed.
   */
  protected get selectableLogMeals(): Log_Meal[] {
    return this.logMeals.filter(m => m.state !== LogRecordState.Skipped);
  }
  /**
   * The meal these ingredients belong to on the New and Edit paths, where there is
   * no meal picker and so no `selectedLogMeal`. The day sum and `splitTuningTargets` use it. Why it is
   * an identity and not the document: `EditedMealRef` in day-nutrient-total.ts.
   */
  private editedMeal?: EditedMealRef;

  /**
   * Display label for a meal in the "Meal" picker (#4317).
   *
   * The picker lists the day's meals, whose names are stored in English
   * ("Meal 1", "Meal 2", …). Without this the dropdown showed English options
   * under a Spanish field label. A name the user typed passes through unchanged.
   */
  protected mealDisplayName(logMeal: Log_Meal): TranslatedString {
    return untranslatedString(formatScheduledMealName(logMeal.name));
  }

  // ---------------------------------------------------------------------------
  // The macro card and its two dials. Full design: design-docs/nutrient-bars.md,
  // "The compact-macros card".
  // ---------------------------------------------------------------------------

  protected readonly MacroCardScope = MacroCardScope;

  protected macroScope: MacroCardScope = MacroCardScope.Food;
  protected macroMode: NutrientDisplayMode = NutrientDisplayMode.Logged;

  /**
   * Whether the MEAL is worth offering as a scope. Without targets there is
   * nothing for it to measure against, and with nutrition tracking off the whole
   * idea does not apply.
   *
   * On its own this no longer decides whether the dials are drawn -- the day can
   * carry them by itself. `canShowScopeDials` is that question.
   *
   * Asked through `holdsAnyTargets`, which carries the reason: an EMPTY target map
   * is truthy while holding nothing, and reading that as "there are targets" is
   * what would label what the user just ate as what they have left to eat.
   */
  protected get canShowMealScope(): boolean {
    return holdsAnyTargets(this.targets) && !!this.targetDisplayNutrients && !!this.userDb.nutritionTracking;
  }

  /**
   * Whether the day is worth offering. The same test as the meal, one level up:
   * the day's own entry has to be loaded and to carry targets of its own.
   *
   * A day is loaded on every path that hands one over: Log mode works it out from
   * its own date; the New and Edit paths are given it by the meal page; and the
   * meal-photo scanner forwards it from whichever page opened the scanner. Where
   * none arrives, the dial has one position fewer rather than offering a reading
   * the page cannot produce.
   *
   * Be careful telling an absent day from an impossible one -- reading the first as
   * the second is the mistake that shipped this scope on the Log path alone, and
   * then again on the scanner. A recipe in `edit-food`, and the recipe scanner it
   * opens, genuinely have no day: a recipe is not eaten on any particular one. Every
   * other way in is opened from something that sits on a day, so if a day is missing
   * there it is missing because nobody passed it, not because none exists.
   */
  protected get canShowDayScope(): boolean {
    return holdsAnyTargets(this.logDay?.targets) && !!this.dayDisplayNutrients && !!this.userDb.nutritionTracking;
  }

  /** Whether either wider scope is on offer, and so whether the dials are drawn at all. */
  protected get canShowScopeDials(): boolean {
    return this.canShowMealScope || this.canShowDayScope;
  }

  /**
   * The scope the card is actually drawn at, which is not always the one the
   * dial remembers.
   *
   * `macroScope` can be restored from a previous visit as `Meal` and then land
   * on a food with no meal targets, where the meal dial is not drawn at all.
   * Following the stored value there would show an empty card with no control
   * on screen to get out of it, so the meal scope is only honoured while it is
   * actually on offer. The stored preference is left alone: the next food that
   * does have a meal still opens on the meal scope, as asked.
   */
  protected get effectiveMacroScope(): MacroCardScope {
    if (this.macroScope === MacroCardScope.Meal && this.canShowMealScope) return MacroCardScope.Meal;
    if (this.macroScope === MacroCardScope.Day && this.canShowDayScope) return MacroCardScope.Day;
    return MacroCardScope.Food;
  }

  /**
   * The scope dial's positions, in order. Built as a list, which is what let
   * `Day` be added as one more entry rather than a redesign.
   */
  protected get macroScopeSegments(): { value: MacroCardScope, label: TranslatedString }[] {
    const segments = [
      { value: MacroCardScope.Food, label: LL.nutrientList.food() },
    ];

    // a scope only gets a position when it has something to measure against, so
    // the dial never offers a reading the page cannot produce
    if (this.canShowMealScope) {
      segments.push({
        value: MacroCardScope.Meal,
        // the plain word, never the meal's own name: it always means the meal this food
        // is going into, and a name the user typed can be any length. It is also the word
        // the Meal screen's own scope dial uses (owner decision, 2026-09-11, #5327).
        label: LL.nutrientList.meal(),
      });
    }
    if (this.canShowDayScope) {
      segments.push({ value: MacroCardScope.Day, label: LL.nutrientList.day() });
    }

    return segments;
  }

  protected get macroCardNutrients(): UiDisplayNutrient[] {
    switch (this.effectiveMacroScope) {
      case MacroCardScope.Meal: return this.targetDisplayNutrients;
      case MacroCardScope.Day: return this.dayDisplayNutrients;
      default: return this.displayNutrients;
    }
  }

  protected get macroCardBarStyle(): NutrientBarStyle {
    // a single food has no targets, so its bar shows how its own calories split
    return this.effectiveMacroScope === MacroCardScope.Food
      ? NutrientBarStyle.Distribution
      : NutrientBarStyle.Targets;
  }

  /**
   * The mode dial means something on every scope that has targets -- the meal and
   * the day -- and nothing on Food, which has none to subtract from. So on Food
   * the card always shows totals whatever the dial last said, and the template
   * switches the dial off there -- still laid out, so the row cannot jump, but
   * unreachable by touch or keyboard.
   *
   * The dial keeps its remembered position while it is off, so switching
   * back to the meal or the day brings back the mode the user last chose. That
   * is why this clamp is still needed rather than merely belt-and-braces: a
   * remembered "Left" would otherwise be applied to a food with no target.
   *
   * The test below is on Food for that reason, NOT on the meal: writing it the
   * other way round silently breaks Total/Left on the day scope.
   */
  protected get macroCardDisplayMode(): NutrientDisplayMode {
    return this.effectiveMacroScope === MacroCardScope.Food ? NutrientDisplayMode.Logged : this.macroMode;
  }

  /**
   * The dial shows the EFFECTIVE scope, so what comes back is the position the
   * user actually tapped; the stored preference is updated from it here.
   */
  protected onMacroScopeChange(scope: MacroCardScope) {
    this.macroScope = scope;
    this.rememberMacroPreference(MACRO_SCOPE_STORAGE_KEY, scope);
  }

  protected onMacroModeChange() {
    this.rememberMacroPreference(MACRO_MODE_STORAGE_KEY, this.macroMode);
  }

  /**
   * Restore the dials to wherever they were left last time.
   *
   * Anything unreadable or unrecognised leaves them at Food and Total, which is
   * where they start anyway -- so a browser that refuses storage costs the user
   * one tap, not an error. The failure is reported as INFO rather than a
   * problem because by the time it is reported the fallback has already run.
   */
  private restoreMacroPreference() {
    const storage = browserLocalStorageOrNull();
    if (!storage) return;

    try {
      const scope = storage.getItem(MACRO_SCOPE_STORAGE_KEY);
      if (scope === MacroCardScope.Food || scope === MacroCardScope.Meal || scope === MacroCardScope.Day) {
        this.macroScope = scope;
      }

      const mode = storage.getItem(MACRO_MODE_STORAGE_KEY);
      if (mode === NutrientDisplayMode.Logged || mode === NutrientDisplayMode.Remaining) {
        this.macroMode = mode;
      }
    } catch (error) {
      this.logging.trackSilentInfo('PortionMacroPreferenceReadFailed', {
        message: (error as Error)?.message,
      });
    }
  }

  private rememberMacroPreference(key: string, value: string) {
    const storage = browserLocalStorageOrNull();
    if (!storage) return;

    try {
      storage.setItem(key, value);
    } catch (error) {
      // reported as a PROBLEM, not info: nothing repaired this. The write
      // simply did not happen, so the next visit silently loses the dial the
      // user set. It is not worth interrupting anyone over -- the dials still
      // work for this visit -- but it is still an unresolved condition.
      this.logging.trackSilentProblem('PortionMacroPreferenceWriteFailed', {
        message: (error as Error)?.message,
      });
    }
  }
  protected selectedLogMeal: Log_Meal;
  protected loadingMeals = false;
  private isExiting = false;
  /** What the ingredient held when the page opened, so the guard can tell an edit from a load. */
  private ingredientAsLoaded: Ingredient;
  protected sourceLogMeal: Log_Meal;  // for copying from another log meal

  /**
   * The grams amount field. #4967 opens the on-screen keypad on it as the page appears, so
   * the user can type the amount straight away instead of hunting for the field and tapping
   * it. Only present when the Amount block is rendered — a log meal preview has none.
   */
  @ViewChild('amountMeasure', { static: false }) private amountMeasure?: CsInputMeasureComponent;

  constructor(
    public navCtrl: NavController,
    public userDb: UserDb,
    public foodDb: FoodDbService,
    private cd: ChangeDetectorRef,
    private popupCtrl: PopupMenuProvider,
    private alertController: AlertController,
    private injector: Injector,
    
    private pubSub: PubSubService,
    private debug: DebugService,
    private logging: LoggingService,
    private auth: AuthService,
    private telemetry: TelemetryService,
    private toastCtrl: ToastController,
    private settings: SettingsService,
  ) {
    super();
  }

  ngOnDestroy() {
    this.docWatcher?.destroy();
  }

  async ngOnInit() {
    if (!this.docWatcher) {
      this.docWatcher = this.userDb.database.createDocWatcher((_changes) => {
        if (this.viewDestroyed) return;
        this.cd.markForCheck();
      });
    }

    // put the macro card's dials back where they were left last time
    this.restoreMacroPreference();

    // simple params - access directly from typed navParams.data
    this.ingredientIndex = this.navParams.data.ingredientIndex;
    this.mode = this.navParams.data.mode;
    // if the caller said what it can apply, honour it; absent means it can apply a split
    this.returnCapability = this.navParams.data.returnCapability
      ?? EditIngredientReturnCapability.SplitOrSingleIngredient;

    // local params - access via data.local
    this.ingredients = this.navParams.data.local?.ingredients || [];
    this.targets = this.navParams.data.local?.targets;

    // if new or log mode, use pending ingredient (not yet in list)
    if (this.mode === EditIngredientMode.New || this.mode === EditIngredientMode.Log) {
      const pendingIngredient: Ingredient = this.navParams.data.local?.pendingIngredient;
      if (pendingIngredient) {
        this.ingredient = deepClone(pendingIngredient);
        // if a source log meal was passed (recent meal confirmation), use it for nutrient display
        if (this.navParams.data.local?.sourceLogMeal) {
          this.sourceLogMeal = this.navParams.data.local.sourceLogMeal;
        }
      } else if (this.mode === EditIngredientMode.Log) {
        // Log mode: create ingredient from foodId or logMealId if no pendingIngredient
        const foodId = this.navParams.data.foodId;
        const logMealId = this.navParams.data.logMealId;
        if (foodId) {
          this.ingredient = await this.createIngredientFromFoodIdAsync(foodId);
        } else if (logMealId) {
          // copying from another log meal - load source meal and create placeholder ingredient
          this.sourceLogMeal = await this.loadSourceLogMealAsync(logMealId);
          if (this.sourceLogMeal) {
            this.ingredient = this.createIngredientFromLogMeal(this.sourceLogMeal);
          }
        }
      }
    } else if (this.ingredientIndex >= 0 && this.ingredientIndex < this.ingredients.length) {
      // deep clone to avoid modifying the original until save
      this.ingredient = deepClone(this.ingredients[this.ingredientIndex]);
    }

    if (!this.ingredient) {
      // nothing loaded, so nothing to lose and nothing to ask about
      this.leaveWithoutAsking.set();
      await this.navCtrl.popAsync();
      return;
    }

    // Log mode: initialize date and load meals
    if (this.mode === EditIngredientMode.Log) {
      await this.initializeLogModeAsync();
    } else {
      // New and Edit arrive from a meal page, which tells us which day it is on
      await this.loadDayForMealModesAsync();
    }

    await this.loadIngredientDataAsync();
    await this.updateDisplayNutrientsAsync();

    this.cd.markForCheck();

    // Snapshot only once the view has actually rendered, not merely once the
    // ingredient has loaded.
    //
    // The amount input renders only after the ingredient exists, and on its own
    // first run `cs-input-measure` can emit `userUnitChange` — when the
    // ingredient carries no unit but the user has a stored preference for this
    // measurement type, which is the ordinary case for a Log-mode ingredient.
    // That emit reaches `onWeightChange`, which writes `weight_unit` onto the
    // ingredient. Snapshotting before that lands records an ingredient the page
    // is about to change by itself, so simply opening one and pressing Back
    // would raise "Discard your changes to this ingredient?" over nothing the
    // user did.
    //
    // Waiting for one render settles it either way round: whether that emit
    // happens before this line or after it, the baseline records what the page
    // has actually settled on.
    await waitForAngularUpdate(this.injector);
    // Snapshot even if the page has been left in the meantime, and deliberately
    // so. `viewDestroyed` means the page was COVERED, not destroyed — tapping
    // "View Details" sets it and coming back clears it, while `ngOnInit` runs
    // only once. Returning early here would leave the baseline unset for the
    // rest of the page's life, and an unset baseline makes the guard answer
    // "nothing changed" — so the user could come back, edit the amount, press
    // Back and lose it in silence. This line touches no view, only data, so
    // there is nothing to guard against.
    this.ingredientAsLoaded = deepClone(this.ingredient);

    this.openAmountKeypad();
  }

  /**
   * Open the on-screen keypad on the amount field as the page appears (#4967).
   *
   * `focus()` rather than the component's `autofocus` input: `autofocus` only moves focus,
   * while `focus()` also starts the keypad, which is what "keypad up on open" asks for. The
   * value already on screen arrives selected, so the first digit typed replaces it — that
   * part is the keypad's own behaviour and needs nothing here.
   *
   * Skipped during E2E runs for the same reason `autofocus` skips them: moving focus while a
   * test is typing produces races. It is skipped on a covered page too — tapping "View
   * Details" and coming back must not re-open the keypad over whatever the user is reading.
   */
  private openAmountKeypad() {
    if (isE2ETest() || this.viewDestroyed) return;
    void this.amountMeasure?.focus();
  }

  private async loadIngredientDataAsync() {
    // a reload may be for another food, so drop the previous food's hint and checkbox until this one is read
    this.refuseHint = undefined;
    this.weighedWithLabel = undefined;
    this.isWeighedWithRefuseTicked = false;
    this.weighedWithNote = undefined;
    this.weighedWithPercent = undefined;
    this.weightsAtUntick = undefined;
    // likewise the previous load's problem: a reload that succeeds must not keep its warning
    this.foodLoadProblem = undefined;
    if (isIngredientReal(this.ingredient)) {
      // if copying from a log meal, skip food loading - we use the meal's data directly
      if (this.sourceLogMeal) {
        this.customIngredientUnits = [];
        this.weight_unit = 'g';
        return;
      }

      // Throw so a read that FAILED arrives as an error rather than as null, and the user is
      // told to try again instead of being told their food is gone
      try {
        this.food = await this.foodDb.getFoodByIdAsync(this.ingredient.foodId, { cacheFood: true, onReadFailure: FoodReadFailurePolicy.Throw });
      } catch (error) {
        // anything that is not a failed food read is unexpected here, so it travels on
        if (!(error instanceof FoodReadFailedError)) throw error;
        this.food = undefined;
        this.foodLoadProblem = LL.errors.userFacing.foodLoadFailed();
        this.reportFoodLoadFailed(error);
        return;
      }
      if (!this.food) {
        // an opted-in caller only sees null for a genuine not-found
        this.foodLoadProblem = LL.ingredientSearch.errors.foodDoesNotExist();
        return;
      }

      this.food.measures = this.food.measures || [];

      this.customIngredientUnits = this.food.measures.map(m => ({
        // id stays the canonical English measure name -- it is the stored identity, compared
        // against ingredient.custom_unit elsewhere. Only the display fields get translated.
        id: m.name,
        description: translateMeasureName(m.name),
        unit1: translateMeasureName(m.name),
        // a translated built-in name can be multiple words (e.g. es "cuarto de galón" for
        // `quart`) -- doNotShortenUnit1 stops cs-input-measure/virtual-keyboard from running
        // it through shortUnitName's word-truncation, the same rule formatServingSize follows
        doNotShortenUnit1: isBuiltInMeasureName(m.name),
        value1: m.weight_g / m.amount,
        fractionDenominatorMax: 8,
      }));

      this.custom_unit = this.customIngredientUnits.length > 0 && this.ingredient.custom_unit;
      this.weight_unit = this.ingredient.weight_unit;
      this.isComplexFood = isComplexFood(this.food);
      this.complexFoodHasParts = this.isComplexFood && hasSplitParts(this.food as ComplexFood);
      // a recipe with no parts gets no Split controls, so the split-time report can no longer
      // fire for it. report it here instead, so we can still find out how recipes end up empty
      // (df-5e2bcf38fbf2)
      if (this.isComplexFood && !this.complexFoodHasParts) {
        this.logging.trackSilentProblem('portion screen: the recipe has no ingredients', {
          foodId: this.food._id,
          status: this.food.status,
        });
      }
      this.checkRealFoodNutrients();
      this.refuseHint = buildRefuseHint(this.food.refuse);
      // if the food says something is not eaten but nothing of it can be read, the stored document is damaged
      if (this.food.refuse != null && this.refuseHint === undefined) {
        this.logging.trackSilentProblem('food refuse unreadable', { foodId: this.food._id });
      }
      this.loadWeighedWithRefuse(this.ingredient, this.food);
    } else if (isIngredientMacro(this.ingredient)) {
      if (this.ingredient.status === undefined) {
        this.ingredient.status = FoodStatus.Custom;
      }
      this.customIngredientUnits = [];
      this.weight_unit = this.ingredient.weight_unit;
      this.checkCustomIngredient();
    }
  }

  /**
   * Report that the food this screen is for could not be read. Asking for a failed read as an
   * error means getFoodByIdAsync no longer reports a network failure itself, so without this
   * the failure would reach nobody. The same two expected-teardown conditions are skipped as
   * in markMealTotalUnreadable, for the same reason.
   */
  private reportFoodLoadFailed(error: FoodReadFailedError) {
    if (this.auth.isLikelySignOutRace() || isDatabaseUnavailable(error.originalError)) return;

    this.logging.trackSilentProblem('PortionFoodLoadFailed', {
      message: error.message,
      foodId: isIngredientReal(this.ingredient) ? this.ingredient.foodId : undefined,
    });
  }

  // Log mode methods
  private async initializeLogModeAsync() {
    // get date from params or use current day
    let dayDate = this.navParams.data.dayDate;
    if (!dayDate) {
      dayDate = this.debug.getNowLocal().toISODate();
    }
    this.dayDate = dayDate;

    // load meals for the selected day. A failed read must not travel, for the same
    // reason `loadDayForMealModesAsync` below catches its own: this runs from
    // `ngOnInit` BEFORE the food is loaded and before `ingredientAsLoaded` is set, so
    // letting it escape aborts the rest of `ngOnInit` and leaves the unsaved-changes
    // baseline unset -- which makes the guard answer "nothing changed" and lets the
    // user type an amount, press Back and lose it with no prompt. Carrying on instead
    // costs an empty meal picker, which is what a day with no meals already looks
    // like. Reported as a PROBLEM: nothing repaired it, and the day is simply unread.
    try {
      await this.loadDayAndSelectDefaultMealAsync(dayDate);
    } catch (error) {
      this.logging.trackSilentProblem('PortionLogDayLoadFailed', {
        message: (error as Error)?.message,
      });
    }

    // if a specific meal was pre-selected, find it
    const selectedMealId = this.navParams.data.selectedMealId;
    if (selectedMealId) {
      // searched in the offered meals, not in every meal on the day, so the picker's
      // value is always one of the options it is showing
      const preSelectedMeal = this.selectableLogMeals.find(m => m._id === selectedMealId);
      if (preSelectedMeal) {
        this.selectedLogMeal = preSelectedMeal;
      } else if (this.logMeals.some(m => m._id === selectedMealId)) {
        // if the caller named a meal that IS on this day but is skipped, select nothing at all.
        // The default chosen above is a DIFFERENT meal, so keeping it would quietly log the food
        // somewhere the user never asked for -- the meal-photo scanner passes this id precisely
        // to keep the food in the meal the scan started from. With nothing selected, pressing Log
        // says there is no meal here to add to, which is true and is something the user can act on.
        this.selectedLogMeal = undefined;
      }
    }

    // no redraw here: ngOnInit redraws right after this returns
    this.applySelectedLogMealToPage();
  }

  /**
   * Copy the selected Log meal into the figures "Impact to Meal Targets" measures
   * against, or empty them when no meal is selected.
   *
   * The one place this happens, because opening the page, changing the day and
   * changing the meal each used to carry their own copy, and the copies had drifted
   * apart. A copy that is missed shows stale figures that look
   * exactly like correct ones. It does not redraw: each caller decides when to.
   */
  private applySelectedLogMealToPage() {
    if (this.selectedLogMeal) {
      this.ingredients = this.selectedLogMeal.ingredients || [];
      this.targets = this.selectedLogMeal.targets;
    } else {
      // no meal to add this food to, so nothing to measure against
      this.ingredients = [];
      this.targets = undefined;
    }
  }

  /**
   * Read the day: its meals, and its own entry carrying its targets.
   *
   * Deliberately does NOT choose a meal. Log mode wants a default selection for its
   * meal picker and asks for one separately; the New and Edit paths arrive from a
   * meal page with the meal already decided and no picker on screen, so selecting
   * one here would invent a choice the user never made.
   */
  private async loadDayAsync(dayDate: string) {
    this.loadingMeals = true;
    this.cd.markForCheck();

    try {
      const entries = await this.userDb.getLogEntriesInRangeAsync(
        DateTime.fromISO(dayDate).startOf('day'),
        DateTime.fromISO(dayDate).endOf('day'),
        this.docWatcher,
        { inLogEntriesTimeZone: true }
      );
      this.logMeals = entries.filter(e => e.type === LogRecordType.Meal) as Log_Meal[];

      // The day's own entry, which carries the day's targets -- what the Day scope
      // measures against. Picked through the SHARED singleton rule rather than by
      // taking the first one found: a day can hold two day-start entries, and
      // answering that question differently on two screens once showed the user two
      // different calorie targets for one day (#4511). Asked through UserDb rather than
      // called directly, because that wrapper also reports the entry it set aside, which
      // nothing did before (df-97a26a40a3e0).
      this.logDay = this.userDb.keepOneEntryPerSingletonSlot(entries, DateTime.fromISO(dayDate))
        .find(e => e.type === LogRecordType.DayStart) as Log_DayStart;
    } finally {
      // in a `finally` because the read THROWS when the database cannot answer --
      // it never returns an empty list -- and leaving this set would spin the
      // page's loading state for ever with nothing saying why
      this.loadingMeals = false;
      this.cd.markForCheck();
    }
  }

  /**
   * Read the day AND choose a meal in it, which is what Log mode's picker needs.
   * Named for the selection because that is its only effect of its own -- the
   * reading is `loadDayAsync`'s, and a caller that wants only the day should call
   * that instead rather than silently acquiring a meal the user never picked.
   */
  private async loadDayAndSelectDefaultMealAsync(dayDate: string) {
    await this.loadDayAsync(dayDate);

    // chosen from the meals the picker actually offers, so nobody is landed on a
    // meal they marked as skipped without picking it -- see `selectableLogMeals`
    const selectable = this.selectableLogMeals;
    if (!this.selectedLogMeal && selectable.length > 0) {
      this.selectedLogMeal = selectable[0];
    }
  }

  /**
   * The day behind a screen opened from a meal page rather than from the day's food
   * list. Without it the macro card's `Day` scope is simply not offered there.
   *
   * The date and the meal id arrive together from `ingredient-list`; the meal id is
   * what `updateDayDisplayNutrients` leaves out of the day's sum, standing in for
   * the `selectedLogMeal` that only Log mode has.
   */
  private async loadDayForMealModesAsync() {
    const dayDate = this.navParams.data.local?.dayDate;
    if (!dayDate) return;

    this.dayDate = dayDate;
    const mealId = this.navParams.data.local?.mealId;
    this.editedMeal = mealId === undefined
      ? undefined
      : { _id: mealId, name: this.navParams.data.local?.mealName };

    // The day is decoration on this path: without it the card shows one scope
    // fewer, which is what every screen with no day behind it already does. So a
    // failed read must not travel. It would abort the rest of ngOnInit -- the food
    // would never load, and `ingredientAsLoaded` would never be set, which makes
    // the unsaved-changes guard answer "nothing changed" and lets the user lose a
    // typed amount by pressing Back. Reported as a PROBLEM rather than info:
    // nothing repaired it, the Day scope is simply missing for this visit.
    try {
      await this.loadDayAsync(dayDate);
    } catch (error) {
      this.logging.trackSilentProblem('PortionDayScopeLoadFailed', {
        message: (error as Error)?.message,
      });
    }
  }

  async handleDayDateChange(newDate: string) {
    this.dayDate = newDate;
    this.selectedLogMeal = undefined;

    // A failed read leaves `logMeals` and `logDay` holding the PREVIOUS day, while
    // `dayDate` above already says the new one -- so the picker would offer yesterday's
    // meals under today's date and the card would measure against yesterday's targets.
    // Emptying both says "this day was not read" in the only way this screen has, and
    // letting the method carry on is what redraws the card; an escaping error would
    // skip the redraw at the foot of this method entirely. Reported for the same
    // reason as the open-the-page case above: nothing repaired it.
    try {
      await this.loadDayAndSelectDefaultMealAsync(newDate);
    } catch (error) {
      this.logMeals = [];
      this.logDay = undefined;
      this.logging.trackSilentProblem('PortionLogDayChangeLoadFailed', {
        message: (error as Error)?.message,
      });
    }

    this.applySelectedLogMealToPage();

    // redrawn either way, because the DAY changed even when no meal came with it.
    // Gated on a selected meal, the card would go on showing the previous day's
    // figures under the new date.
    await this.updateDisplayNutrientsAsync();
  }

  handleMealChange() {
    this.applySelectedLogMealToPage();
    // redrawn even with no meal selected, so the card never keeps the previous meal's figures
    void this.updateDisplayNutrientsAsync();
  }

  private async createIngredientFromFoodIdAsync(foodId: string): Promise<IngredientReal | null> {
    const food = await this.foodDb.getFoodByIdAsync(foodId, { cacheFood: true, onReadFailure: FoodReadFailurePolicy.TreatAsMissing });
    if (!food) {
      return null;
    }

    const ingredient: IngredientReal = {
      type: IngredientType.Real,
      foodId: food._id,
      name: food.name,
      weight_g: 100,
      weight_unit: undefined,
      status: food.status || this.foodDb.getFoodStatus(food._id),
    };

    if (isRecipe(food)) {
      // getFoodByIdAsync auto-fixes totalWeight_g, but use utility as fallback
      ingredient.weight_g = food.totalWeight_g || calculateRecipeTotalWeight(food);
    } else {
      let measure: FoodMeasure;
      // make sure to handle eventual damaged data in the database!
      if (food.measures && typeof food.measures === 'object') {
        measure = food.measures[0];
        if (measure) {
          ingredient.weight_g = measure.weight_g || ingredient.weight_g;
          ingredient.custom_unit = measure.name;
        }
      }
    }

    // apply cached measure for all foods (including recipes)
    if (food.local?.lastUsedMeasureUnitName) {
      ingredient.custom_unit = food.local.lastUsedMeasureUnitName;
    }
    if (food.local?.lastUserMeasureWeight_g) {
      ingredient.weight_g = food.local.lastUserMeasureWeight_g;
    }
    if (food.local?.lastUsedWeightUnit) {
      ingredient.weight_unit = food.local.lastUsedWeightUnit;
    }

    await this.foodDb.markFoodAsUsedAsync(food);
    return ingredient;
  }

  /**
   * Read the recent meal the user picked to log again. Answers null when it cannot, and in that
   * case has already told the user and Sentry (df-d299e54df3ec): the caller then closes the
   * screen, and without this the user saw it open and shut with nothing added and nothing said.
   */
  private async loadSourceLogMealAsync(logMealId: string): Promise<Log_Meal | null> {
    const entry = await this.userDb.backlogCache.getEntryByIdAsync(logMealId);
    if (entry && entry.type === LogRecordType.Meal) {
      return entry as Log_Meal;
    }
    // the two cases stay apart in the report: a missing entry points at the cache or sync,
    // an entry of another type points at whoever handed us this id
    const reason = entry ? 'the entry is not a meal' : 'no entry with this id was found';
    this.logging.trackAbnormalError(
      `Portion screen: the recent meal picked for logging could not be read (${reason})`,
      LL.errors.userFacing.recentMealCouldNotBeOpened()
    );
    return null;
  }

  private createIngredientFromLogMeal(logMeal: Log_Meal): IngredientReal {
    // create a placeholder ingredient that represents the log meal
    // this will be shown as a single item, but split into individual ingredients on save
    const totalCalories = logMeal.nutrients?.ENERC_KCAL || 0;
    return {
      type: IngredientType.Real,
      foodId: logMeal._id,
      name: logMeal.name,
      weight_g: logMeal.ingredients?.reduce((sum, ing) => sum + (ing.weight_g || 0), 0) || 0,
      kcal: totalCalories,
      status: FoodStatus.Recent,
    };
  }

  /**
   * Warn when the food the user is about to log states calories that its own protein, fat
   * and carbohydrates cannot add up to (#4541).
   *
   * This is the same condition that already warns somebody typing their own food, applied
   * to a food that came out of the shared database -- until now the app noticed it (it
   * quietly pushed the food down the search results) but never said so. This screen is
   * where it matters, because every way of adding a food passes through here before a
   * number lands in the log.
   *
   * The wording differs from the custom-food warning on purpose: the user cannot correct an
   * official food, so it tells them what they can do instead.
   */
  private checkRealFoodNutrients() {
    this.inconsistentNutrientsWarning = undefined;
    if (!this.food?.nutrients) {
      return;
    }
    // the check compares against absolute calories per 100 g, and a recipe stores its
    // nutrients per total weight, so scale first -- the same reference weight
    // updateDisplayNutrientsAsync uses
    const referenceWeight_g = getFoodReferenceWeight(this.food);
    if (!referenceWeight_g) {
      return;
    }
    // only scale when it would actually change something. for anything but a recipe the
    // reference weight IS 100, so scaling is the identity -- except that it divides every
    // key, which turns a nutrient present with an explicit null into 0 and one present with
    // undefined into NaN. Either then satisfies the check's "all four macros are present"
    // test while counting as no calories, which is how a food the search list rates as
    // merely doubtful could be accused outright on this screen. Passing the nutrients
    // through unchanged keeps the two screens judging the same record the same way.
    const nutrientsPer100g = referenceWeight_g === 100
      ? this.food.nutrients
      : scaleNutrientsToPer100g(this.food.nutrients, referenceWeight_g);
    const expectedCalories = getGrossMismatchExpectedCalories(nutrientsPer100g);
    if (expectedCalories !== undefined) {
      this.inconsistentNutrientsWarning = LL.editIngredient.inconsistentNutrientsWarning({ expectedCalories });
    }
  }

  private checkCustomIngredient() {
    if (!isIngredientMacro(this.ingredient)) return;

    const ingredient = this.ingredient;
    this.errorMessage = undefined;
    if (NutrientDefinitionsOrdered.some(d => d.required && isNutrientValueEntered(ingredient.nutrients[d.tag]) && d.tag !== INFOODS.Calories)) {
      let { correct, expectedCalories } = checkNutrientCalories(ingredient.nutrients);
      if (!correct) {
        this.errorMessage = LL.editIngredient.caloriesMismatchWarning({ expectedCalories });
      }
    }
  }

  private async updateDisplayNutrientsAsync() {
    if (isIngredientReal(this.ingredient) && this.food) {
      // getFoodByIdAsync auto-fixes recipe totalWeight_g
      const refWeight = getFoodReferenceWeight(this.food);
      this.displayNutrients = FormatNutrients(this.food.nutrients, this.userDb, {
        actual_g: this.ingredient.weight_g,
        ref_g: refWeight
      });
    } else if (isIngredientReal(this.ingredient) && this.sourceLogMeal) {
      // copying from log meal - use the meal's nutrients (already calculated)
      this.displayNutrients = FormatNutrients(this.sourceLogMeal.nutrients || {}, this.userDb, {
        actual_g: this.ingredient.weight_g,
        ref_g: this.ingredient.weight_g
      });
    } else if (isIngredientMacro(this.ingredient)) {
      // a typed-in food stores its numbers per 100 g, so scale them to the amount
      // being logged here, and then pass `ref_g` equal to `actual_g` below so that
      // FormatNutrients does not scale the same numbers a second time
      const unusable = newUnusableNutrientInput();
      // the display variant keeps a nutrient nobody measured as "not measured", so it shows
      // as "--" rather than as a 0 the record never stated
      const nutrientsModified = nutrientsForWeightForDisplay(this.ingredient.nutrients, this.ingredient.weight_g, 100, unusable);
      reportUnusableNutrientInput(unusable, this.logging, 'amount-screen.typed-in-ingredient-display', {
        ingredientName: this.ingredient.name,
        weight_g: this.ingredient.weight_g,
      });
      this.displayNutrients = FormatNutrients(nutrientsModified, this.userDb, {
        actual_g: this.ingredient.weight_g,
        ref_g: this.ingredient.weight_g
      });
      this.displayNutrients = this.displayNutrients.filter(e => e.required || e.tag === INFOODS.Cholesterol);

      this.displayNutrientsCustom = FormatNutrients(this.ingredient.nutrients, this.userDb, {
        actual_g: this.ingredient.weight_g,
        ref_g: this.ingredient.weight_g
      });
      this.displayNutrientsCustom = this.displayNutrientsCustom.filter(e => e.required || e.tag === INFOODS.Cholesterol);
    }

    // This meal's running total, which BOTH wider scopes need: the meal scope
    // shows it directly, and the day scope puts it in place of this meal's stored
    // figures. A day with targets can sit above a meal with none, so it is not
    // enough to compute this only when the meal has targets -- doing that left the
    // day total missing the very food being typed.
    if (this.targets || this.logDay?.targets) {
      await this.calculateTotalNutrientsAsync();
    }

    if (this.targets) {
      // a running total that could not be added up in full is not drawn at all: the
      // reading would be short by the food that could not be read, with nothing on screen
      // saying so. Leaving the list unset is what takes this position off the scope dial
      // -- see canShowMealScope.
      this.targetDisplayNutrients = this.mealTotalIncomplete ? undefined : FormatNutrients(this.totalNutrients, this.userDb, {
        targets: this.targets
      });
    }

    this.updateDayDisplayNutrients();

    this.cd.markForCheck();
  }

  /**
   * The day's nutrients as they WILL BE once this food is logged, against the
   * day's own targets.
   *
   * The arithmetic, and why the day entry's own stored totals cannot be used, is
   * in `sumDayNutrientsWithEditedMeal` -- kept apart from this page because it is
   * the one calculation here that can be wrong without anything looking broken,
   * and so the one worth testing on its own.
   */
  private updateDayDisplayNutrients() {
    // the day reading puts this meal's running total in place of the meal's stored
    // figures, so a total that could not be added up in full leaves the DAY short by the
    // same missing food. Dropped rather than drawn short, for the same reason the meal
    // reading is -- see markMealTotalUnreadable.
    if (!this.logDay?.targets || this.mealTotalIncomplete) {
      this.dayDisplayNutrients = undefined;
      return;
    }

    // In Log mode with no meal picked, pressing Log stores nothing -- it says there is no
    // meal here to add to -- so the pending food must not be added to the day figure either.
    // Otherwise the card promises a rise that saving cannot produce, which is the very thing
    // `sumDayNutrientsWithEditedMeal` refuses to do for a SKIPPED meal. The two are the same
    // case seen from opposite ends: a day whose meals are all skipped used to arrive here with
    // one of them selected, and now arrives with nothing selected.
    // Passing no meal means something different on the New and Edit paths -- there the food IS
    // going to be saved, into a meal that may not be in the day yet -- so this is gated on Log
    // mode rather than on the meal being absent.
    const nothingWillBeSaved = this.mode === EditIngredientMode.Log && !this.selectedLogMeal;

    // Log mode names the meal through its picker; the meal paths were handed it
    const dayNutrients = sumDayNutrientsWithEditedMeal(
      this.logMeals, this.selectedLogMeal ?? this.editedMeal,
      nothingWillBeSaved ? {} : this.totalNutrients,
      DateTime.fromISO(this.dayDate));

    this.dayDisplayNutrients = FormatNutrients(dayNutrients, this.userDb, {
      targets: this.logDay.targets
    });
  }

  private async calculateTotalNutrientsAsync() {
    // increment calculation counter to detect stale calculations
    const myCalculationId = ++this.calculationId;
    const newTotalNutrients: Nutrients = {};
    // one collector for the whole pass, reported once at the end rather than per
    // ingredient -- a meal with several damaged foods is one condition to look into. Each
    // ingredient scales into its own collector first and is merged in with a label, so the
    // report can say which food held the bad value (#6322)
    const unusable = newUnusableNutrientInput();

    for (let i = 0; i < this.ingredients.length; i++) {
      const ing = i === this.ingredientIndex ? this.ingredient : this.ingredients[i];

      if (ing.weight_g === undefined || isNaN(ing.weight_g)) continue;

      if (isIngredientReal(ing)) {
        let food: Food;
        try {
          // getFoodByIdAsync auto-fixes recipe totalWeight_g. Throw so a read
          // that FAILED arrives as an error rather than as null: without it a moment of bad
          // connectivity is indistinguishable from a deleted food, and this running total --
          // which both the meal reading and the day reading are drawn from -- is shown short
          // by whatever that ingredient held, with nothing saying so.
          food = await this.foodDb.getFoodByIdAsync(ing.foodId, { cacheFood: true, onReadFailure: FoodReadFailurePolicy.Throw });
        } catch (error) {
          // anything that is not a failed food read is unexpected here, so it travels on --
          // BEFORE the staleness check below, which would otherwise discard it in silence on
          // any pass a newer calculation had already overtaken
          if (!(error instanceof FoodReadFailedError)) throw error;

          // if a newer calculation started, abort this one. Its own read meets the same
          // failure and marks the reading itself, so nothing is lost by staying quiet here
          if (myCalculationId !== this.calculationId) return;

          // the food is not missing, it just could not be read this time. The total this
          // pass would publish is already known to be short, so publish nothing at all --
          // see markMealTotalUnreadable.
          this.markMealTotalUnreadable(ing, error);
          return;
        }

        // if a newer calculation started, abort this one
        if (myCalculationId !== this.calculationId) return;

        if (!food) {
          // an opted-in caller only ever sees null for a genuine not-found, so the food
          // really is gone and this ingredient can contribute nothing. The reading stays on
          // screen, short by a food that no longer exists, because that is the only figure
          // there is -- but it is reported, the same way the saved total reports it.
          this.reportMealTotalMissingFood(ing);
          continue;
        }

        addIngredientIntoMealTotal(newTotalNutrients, ing, food, unusable, describeIngredientForReport(ing, i));
      } else if (isIngredientMacro(ing)) {
        addIngredientIntoMealTotal(newTotalNutrients, ing, undefined, unusable, describeIngredientForReport(ing, i));
      }
    }

    // in New/Log mode, the pending ingredient is not in the ingredients array yet
    // so we need to add it separately
    if ((this.mode === EditIngredientMode.New || this.mode === EditIngredientMode.Log) && this.ingredient) {
      if (this.ingredient.weight_g !== undefined && !isNaN(this.ingredient.weight_g)) {
        // the pending ingredient has no position in the meal yet, so its label says so
        if (isIngredientReal(this.ingredient) && this.food) {
          addIngredientIntoMealTotal(newTotalNutrients, this.ingredient, this.food, unusable, `${this.ingredient.foodId} (new ingredient)`);
        } else if (isIngredientReal(this.ingredient) && this.sourceLogMeal?.nutrients) {
          // a meal already logged stores what was actually eaten, so its numbers are
          // absolute and are added as they are -- there is no reference weight to divide by.
          // This is the ONE path whose numbers never pass through nutrientsForWeight, so the
          // collector handed to addNutrientsInto is the only thing that can see them.
          const loggedMealUnusable = newUnusableNutrientInput();
          addNutrientsInto(newTotalNutrients, this.sourceLogMeal.nutrients, loggedMealUnusable);
          mergeUnusableNutrientInput(unusable, loggedMealUnusable, `logged meal ${this.sourceLogMeal._id}`);
        } else if (isIngredientMacro(this.ingredient)) {
          addIngredientIntoMealTotal(newTotalNutrients, this.ingredient, undefined, unusable, 'typed-in (new ingredient)');
        }
      }
    }

    // if a newer calculation started, abort this one
    if (myCalculationId !== this.calculationId) return;

    // report anything the scaling helpers had to skip. Reported here rather than beside
    // each call so one pass over a meal is one report, and only for a pass that is
    // actually publishing its total -- an overtaken pass is re-run and reports then.
    reportUnusableNutrientInput(unusable, this.logging, 'amount-screen.meal-total', {
      mealId: this.selectedLogMeal?._id ?? this.editedMeal?._id,
      ingredientCount: this.ingredients.length,
    });

    // round nutrients and update the result
    let nutrientKeys = Object.keys(newTotalNutrients);
    for (let key of nutrientKeys) {
      newTotalNutrients[key] = RoundToDecimals(newTotalNutrients[key], 4);
    }
    this.totalNutrients = newTotalNutrients;
    // every food was read, so the two wider readings are trustworthy again
    this.mealTotalIncomplete = false;
  }

  /**
   * Record that this meal's running total could not be added up, because a food the meal
   * already holds could not be READ -- not because that food is gone. Why both wider
   * readings vanish rather than being drawn short is in `design-docs/nutrient-bars.md`.
   *
   * No alert, and reported only ONCE per visit to this screen, because this runs on every
   * keypress in the amount field and a failing read retries after 2 seconds.
   */
  private markMealTotalUnreadable(ingredient: IngredientReal, error: FoodReadFailedError) {
    this.mealTotalIncomplete = true;
    if (this.reportedMealTotalReadFailure) return;

    // two conditions reach here that getFoodByIdAsync deliberately does NOT report, because
    // both are expected teardown rather than a problem: a read still in flight when the user
    // signs out, and the local database shutting down or still recovering -- which a warm
    // resume back onto this screen meets routinely. Reporting them here would undo that
    // decision from the outside. Hiding the two readings is right either way; only the
    // report is not.
    if (this.auth.isLikelySignOutRace() || isDatabaseUnavailable(error.originalError)) return;

    this.reportedMealTotalReadFailure = true;
    this.logging.trackSilentProblem('PortionMealTotalReadFailed', {
      // the cause travels with the report: a network blip, an iOS "Load failed" and an
      // unexpected read error all arrive here as the same class, and without this a reader
      // would have to find the separate getFoodByIdAsync issue at a matching timestamp to
      // tell them apart. FoodReadFailedError's message already carries the original.
      message: error.message,
      mealId: this.selectedLogMeal?._id ?? this.editedMeal?._id,
      foodId: ingredient.foodId,
      ingredientWeight_g: ingredient.weight_g,
    });
  }

  /**
   * Record that an ingredient's food is genuinely GONE, so the meal and day readings on
   * screen are short by whatever it held.
   *
   * Nothing else says that: the failed lookup is reported inside `getFoodByIdAsync`, but
   * that says nothing about a total the user is reading being wrong. Once per visit, for
   * the same reason as above.
   */
  private reportMealTotalMissingFood(ingredient: IngredientReal) {
    if (this.reportedMealTotalMissingFood) return;
    this.reportedMealTotalMissingFood = true;
    this.logging.trackSilentProblem('PortionMealTotalMissingFood', {
      mealId: this.selectedLogMeal?._id ?? this.editedMeal?._id,
      foodId: ingredient.foodId,
      ingredientWeight_g: ingredient.weight_g,
    });
  }

  /**
   * Set up the "Weighed with peel" checkbox for this row (#5544). A row already ticked keeps its own
   * stored percent, even if the food's share has since been corrected. A new row starts ticked when
   * the user last logged this food that way; `weight_g` is left as the eaten weight either way, and
   * the typed weight is worked back from it.
   */
  private loadWeighedWithRefuse(ingredient: IngredientReal, food: Food) {
    const stored = readWeighedWithRefuse(ingredient);
    // if the row carries a flag nothing can read, it is damaged: say so, and drop it so the row reads as unticked
    if (stored.damaged) {
      this.logging.trackSilentProblem('ingredient weighedWithRefuse unreadable', { foodId: ingredient.foodId });
      delete ingredient.weighedWithRefuse;
    }
    this.weighedWithPercent = stored.percent ?? usableRefusePercent(food.refuse);
    if (this.weighedWithPercent === undefined) {
      return;
    }
    this.weighedWithLabel = buildWeighedWithLabel(food.refuse);

    const isNewRow = this.mode === EditIngredientMode.New || this.mode === EditIngredientMode.Log;
    if (stored.percent !== undefined) {
      this.isWeighedWithRefuseTicked = true;
    } else if (isNewRow && food.local?.lastWeighedWithRefuse === true) {
      // the user weighed this food with its peel last time, so start ticked. The default amount is
      // the eaten weight they logged then, so the typed weight shown is what they typed then.
      this.isWeighedWithRefuseTicked = true;
      ingredient.weighedWithRefuse = { percent: this.weighedWithPercent };
    }
    if (this.isWeighedWithRefuseTicked) {
      this.typedWeight_g = typedWeight(ingredient.weight_g, this.weighedWithPercent);
    }
    this.refreshWeighedWithNote();
  }

  /** tick or untick "Weighed with peel": the number in the grams field stays what the user typed */
  async onWeighedWithRefuseChange(checked: boolean) {
    if (!isIngredientReal(this.ingredient) || this.weighedWithPercent === undefined) return;
    this.isWeighedWithRefuseTicked = checked;
    if (checked) {
      // the weight on screen was taken with the peel: keep it as typed, and count only what is eaten
      this.typedWeight_g = this.ingredient.weight_g;
      this.ingredient.weighedWithRefuse = { percent: this.weighedWithPercent };
      // if the number is still the one that was in the field when the box was unticked, put back
      // that exact eaten weight rather than working it out again
      const atUntick = this.weightsAtUntick;
      this.ingredient.weight_g = atUntick !== undefined && atUntick.typed_g === this.typedWeight_g
        ? atUntick.eaten_g
        : eatenWeight(this.typedWeight_g, this.weighedWithPercent);
      this.weightsAtUntick = undefined;
    } else {
      // the typed number now counts as eaten in full
      this.weightsAtUntick = { typed_g: this.typedWeight_g, eaten_g: this.ingredient.weight_g };
      this.ingredient.weight_g = this.typedWeight_g;
      delete this.ingredient.weighedWithRefuse;
    }
    // the typed number is what the user sees and must not be worked out again from the eaten weight
    await this.onWeightChange({ fromTypedWeight: true });
  }

  /** the grams field changed while "Weighed with peel" is ticked: convert the typed weight to the eaten weight */
  async onTypedWeightChange() {
    if (isIngredientReal(this.ingredient) && this.isWeighedWithRefuseTicked && this.weighedWithPercent !== undefined) {
      this.ingredient.weight_g = eatenWeight(this.typedWeight_g, this.weighedWithPercent);
    }
    await this.onWeightChange({ fromTypedWeight: true });
  }

  /** rebuild the "Counted as 88 g eaten" line from the current eaten weight, or clear it while the box is unticked */
  private refreshWeighedWithNote() {
    // if unticked, or the grams field is empty or being typed into, there is no weight to state
    if (!this.isWeighedWithRefuseTicked || this.weighedWithPercent === undefined || !Number.isFinite(this.ingredient.weight_g)) {
      this.weighedWithNote = undefined;
      return;
    }
    const eatenText = formatServingSize(
      { name: undefined, amount: undefined, weight_g: this.ingredient.weight_g },
      this.settings.locale,
      { weightUnit: this.weight_unit || 'g' },
    );
    this.weighedWithNote = buildWeighedWithNote(eatenText, this.weighedWithPercent, this.food?.refuse);
  }

  async onWeightChange(options: { fromTypedWeight?: boolean } = {}) {
    // if the eaten weight was changed some other way (the measure field) while the box is ticked,
    // the typed weight follows it, so the two fields never disagree
    if (!options.fromTypedWeight && this.isWeighedWithRefuseTicked && this.weighedWithPercent !== undefined) {
      this.typedWeight_g = typedWeight(this.ingredient.weight_g, this.weighedWithPercent);
    }
    this.refreshWeighedWithNote();
    if (isIngredientReal(this.ingredient)) {
      let customUnitUsed = this.customIngredientUnits.some(u => u.id == this.custom_unit);

      if (customUnitUsed) {
        this.ingredient.custom_unit = this.custom_unit;
        this.ingredient.weight_unit = this.weight_unit;
      } else {
        this.ingredient.weight_unit = this.weight_unit;
        this.ingredient.custom_unit = null;
      }

      if (!this.ingredient.custom_unit) {
        delete this.ingredient.custom_unit;
      }
    } else if (isIngredientMacro(this.ingredient)) {
      this.ingredient.weight_unit = this.weight_unit;
    }

    if (!this.ingredient.weight_unit) {
      delete this.ingredient.weight_unit;
    }

    await this.updateDisplayNutrientsAsync();
  }

  async onCustomNutrientChange() {
    // a cleared field writes undefined into the map; take it out before anything reads the
    // map, or it is reported as a damaged stored value (see dropClearedNutrientInputs)
    if (isIngredientMacro(this.ingredient)) {
      dropClearedNutrientInputs(this.ingredient.nutrients);
    }
    this.checkCustomIngredient();
    await this.updateDisplayNutrientsAsync();
  }

  handleCustomFoodNameChange() {
    // just triggers change detection
    this.cd.markForCheck();
  }

  async handleMoreOptions(event: Event) {
    let buttons: PopupMenuButton[] = [];

    if (isIngredientReal(this.ingredient)) {
      const ingredient = this.ingredient;

      buttons.push({
        text: ingredient.locked
          ? LL.ingredientSearch.buttons.unlock()
          : LL.ingredientSearch.buttons.lock(),
        icon: ingredient.locked ? 'lock-open' : 'lock-closed',
        handler: () => {
          ingredient.locked = !ingredient.locked;
          this.cd.markForCheck();
        },
      });

      // the same getter as the Split buttons in the template, so the two cannot drift: the food
      // has to have parts to split into AND the caller has to be able to apply the list a split
      // hands back. `canSplitFood` also says yes for a log meal being copied, which this menu
      // never shows for (the template hides the ⋯ button then).
      if (this.canSplitFood) {
        buttons.push({
          text: LL.common.buttons.split(),
          icon: 'split',
          handler: (event) => { if (event) lockClick(event, this.handleSplit()); },
        });
      }
    }

    buttons.push({
      text: LL.common.buttons.remove(),
      icon: 'trash',
      handler: (event) => { if (event) lockClick(event, this.handleDelete()); },
    });

    void this.popupCtrl.createMenu({
      buttons: buttons,
      targetFromElement: event.target as HTMLElement,
    });
  }

  async handleSplit() {
    if (!isIngredientReal(this.ingredient) || !isComplexFoodById(this.ingredient.foodId)) {
      return;
    }

    const food = await this.foodDb.getFoodByIdAsync(this.ingredient.foodId, { cacheFood: true, onReadFailure: FoodReadFailurePolicy.TreatAsMissing });
    if (!food || !isComplexFood(food)) return;

    try {
      // pass the weight of the entry being split, so the user gets their portion of the recipe
      // rather than the whole recipe (#4427)
      const { ingredients: subIngredients, basedOnFoodId } = await this.foodDb.getSubIngredientsAsync(
        food as ComplexFood,
        { portionWeight_g: this.ingredient.weight_g },
      );
      // nothing to split into: stay on the screen rather than close it with an empty answer
      // nobody can apply. the service has already reported why it was empty (df-5e2bcf38fbf2)
      if (subIngredients.length === 0) return;

      const result: EditIngredientResult = {
        action: 'split',
        splitIngredients: subIngredients,
        basedOnFoodId,
      };
      this.leaveWithoutAsking.set();
      await this.navCtrl.popAsync(result);
    } catch (e) {
      // a bare console.error here reached neither Sentry nor the user, so a split that
      // failed -- usually because the recipe names a food that is no longer in the
      // database -- looked exactly like a button that does nothing.
      this.logging.trackAbnormalException(e, LL.errors.userFacing.splitIntoFoodsFailed());
    }
  }

  // Get sub-ingredients from either recipe or log meal
  private async getSplitIngredientsAsync(options?: { targetsForTuning?: NutrientTargets }): Promise<{
    ingredients: Ingredient[];
    basedOnFoodId?: string;
    tunedExistingIngredients?: Ingredient[];
    /**
     * Set when the tuner CRASHED rather than answering. The split still happened and the
     * ingredients above are the untuned ones; `handleSplitAsync` is the one place that decides
     * what the user is told about it (df-fc1cd793ae3b).
     */
    tuningError?: string;
    /**
     * Set when the tuner ran correctly and found no combination of these foods that fits the
     * targets, saying which dead end it was. The ingredients are the untuned ones; as with
     * `tuningError`, `handleSplitAsync` decides what the user is told (df-c0a73cdcaa46).
     */
    tuningFoundNoSolution?: CannotTuneReason;
  }> {
    // if copying from a log meal, use its ingredients
    if (this.sourceLogMeal) {
      // copies, never the stored meal's own objects: the meal screen edits its rows in place
      // (Lock Food), which would otherwise change the earlier meal too (#6188). The same
      // function Add Again uses, so both routes drop the same unusable entries.
      const ingredients = ingredientsToRepeat(this.sourceLogMeal);
      const basedOnFoodId = this.sourceLogMeal.basedOnFoodId;

      // if tuning requested, run auto-tune on the meal's ingredients
      if (options?.targetsForTuning && ingredients.length > 0) {
        const tuningSource: MealTunerSource = {
          _id: this.sourceLogMeal._id,
          ingredients: deepClone(ingredients),
        };
        const tuningParams = await this.foodDb.prepareTuningParamsFoodsAsync([tuningSource]);
        tuningParams.targets = options.targetsForTuning;

        const tuner = new MealTuner();
        const results = tuner.solve(tuningParams);

        // The tuner crashed. It catches everything it throws and hands the message back here
        // instead, so an empty answer alone cannot tell a crash apart from the honest "no
        // combination of these foods fits". Carrying the message up is what stops the crash
        // being thrown away entirely, which is what df-fc1cd793ae3b was.
        //
        // Asked through `outcome`, the same way `runAutoTuneAsync` does it in edit-meal.ts: a failed
        // solve carries no meal list at all, so reading the answer below without this check does
        // not compile. `solve` describes every value it catches, so the message carried up from
        // here is never empty.
        if (results.outcome === MealTunerOutcome.Failed) {
          return { ingredients, basedOnFoodId, tuningError: results.error };
        }

        if (results.meals.length > 0) {
          const tunedMeal = results.meals[0];
          const tunedIngredients = deepClone(ingredients);
          for (let i = 0; i < tunedMeal.ingredients.length; i++) {
            const tunedData = tunedMeal.ingredients[i];
            const ing = tunedIngredients[i];
            if (isIngredientReal(ing)) {
              ing.weight_g = tunedData.tunedMeasures * tunedData.gramsPerMeasure;
            }
          }
          return { ingredients: tunedIngredients, basedOnFoodId };
        }

        // the tuner ran and found no combination that fits. hand the untuned ingredients back
        // with the reason, so the user is told rather than left with unchanged amounts
        // (df-c0a73cdcaa46).
        return { ingredients, basedOnFoodId, tuningFoundNoSolution: classifyCannotTune(ingredients) };
      }

      return { ingredients, basedOnFoodId };
    }

    // otherwise, split the recipe (with optional tuning)
    if (!isIngredientReal(this.ingredient)) return { ingredients: [] };
    const food = await this.foodDb.getFoodByIdAsync(this.ingredient.foodId, { cacheFood: true, onReadFailure: FoodReadFailurePolicy.TreatAsMissing });
    if (!food || !isComplexFood(food)) return { ingredients: [] };

    // pass the weight of the entry being split, so the user gets their portion of the recipe
    // rather than the whole recipe (#4427). this applies on the auto-tune path too — the tuner
    // should start from what the user logged.
    const serviceOptions = {
      portionWeight_g: this.ingredient.weight_g,
      // if tuning, include existing meal ingredients for combined tuning (like old AutoTune flow)
      ...(options?.targetsForTuning
        ? {
          targetsForTuning: options.targetsForTuning,
          existingIngredients: this.ingredients || [],
          replacedIngredientIndex: this.splitReplacesListedIngredient ? this.ingredientIndex : undefined,
        }
        : {}),
    };

    return await this.foodDb.getSubIngredientsAsync(food as ComplexFood, serviceOptions);
  }

  /**
   * Whether the food being split is itself one of `this.ingredients` -- true only when an existing
   * row of the meal was opened for editing, which is the one case `ngOnInit` loads it from
   * that list. New and Log mode split a pending food that is not in the list yet.
   */
  private get splitReplacesListedIngredient(): boolean {
    return this.mode !== EditIngredientMode.New
      && this.mode !== EditIngredientMode.Log
      && this.ingredientIndex >= 0
      && this.ingredientIndex < this.ingredients.length;
  }

  handleSplitIntoFoods() {
    void this.handleSplitAsync();
  }

  handleSplitAndAutoTune() {
    // with no targets to aim at there is nothing to tune towards, so this is a plain split. the
    // template does not offer this button in that state -- see `canAutoTuneSplit` -- and the
    // guard is repeated here so the method is right on its own terms rather than relying on
    // every future caller having asked the same question first (df-fc1cd793ae3b).
    if (!this.canAutoTuneSplit) {
      void this.handleSplitAsync();
      return;
    }

    void this.handleSplitAsync({ targetsForTuning: this.splitTuningTargets });
  }

  private async handleSplitAsync(options?: { targetsForTuning?: NutrientTargets }) {
    try {
      const {
        ingredients: subIngredients,
        basedOnFoodId,
        tunedExistingIngredients,
        tuningError,
        tuningFoundNoSolution,
      } = await this.getSplitIngredientsAsync(options);

      // the split went ahead but the tuner crashed on the way, so the amounts came back exactly
      // as they went in. say so: without this the user pressed a button promising to tune their
      // amounts, got an untuned split, and nothing anywhere recorded that anything had gone
      // wrong (df-fc1cd793ae3b).
      //
      // the split is NOT undone, which is where this differs from the meal editor's Auto-Tune.
      // there the user agreed to "split and tune" as one action on a meal that was already
      // saved, so it can put the meal back. here the split is the thing the user asked for and,
      // outside Log mode, has not been saved anywhere yet -- taking it away would leave them
      // with nothing at all for a failure that is ours.
      if (tuningError !== undefined) {
        this.logging.trackAbnormalError(
          `Split and Auto-Tune: MealTuner.solve failed: ${tuningError}`,
          LL.errors.userFacing.splitTuningFailed(),
        );
      }

      if (subIngredients.length === 0) return;

      // the tuner ran fine and honestly found no amounts that fit the targets. the user is told
      // once the split has actually landed -- see `showSplitTuneNoSolutionToast` (df-c0a73cdcaa46).
      const tellNoSolution = tuningFoundNoSolution !== undefined
        ? () => this.showSplitTuneNoSolutionToast(tuningFoundNoSolution)
        : undefined;

      // if in Log mode, add to meal and navigate. the tuned copies of the meal's own rows go with
      // them: the tuner sized the parts and those rows as one set, against what the whole meal
      // should contain, so saving only the parts stores a meal that misses the figure it was just
      // tuned to -- with nothing telling the user (df-0a55eaa0a611). The Edit/New branch below has
      // always passed both halves back to `ingredient-list`, which applies them the same way.
      if (this.mode === EditIngredientMode.Log) {
        await this.splitAndSaveToMealAsync(subIngredients, basedOnFoodId, {
          onSaved: tellNoSolution,
          tunedExistingIngredients,
        });
      } else {
        // in Edit/New mode, return split result to parent
        const result: EditIngredientResult = {
          action: 'split',
          splitIngredients: subIngredients,
          basedOnFoodId,
          tunedExistingIngredients,
        };
        tellNoSolution?.();
        this.leaveWithoutAsking.set();
        await this.navCtrl.popAsync(result);
      }
    } catch (e) {
      // same as handleSplit above: report it rather than writing to a console nobody in
      // production can read. This path also covers the save that logs the split foods into
      // the meal, so a failure here can mean the user's food never landed.
      this.logging.trackAbnormalException(e, LL.errors.userFacing.splitIntoFoodsFailed());
    }
  }

  /**
   * Tell the user that Split & Auto-Tune split their foods but the tuner found no amounts that fit
   * the targets, and what they can change (df-c0a73cdcaa46).
   *
   * Call it only once the split has landed: in Log mode after the meal saved, otherwise just before
   * the page hands the split back. Shown any earlier, it would claim a split that a failed save then
   * undid. Nothing goes to Sentry -- an honest "nothing fits" is not a fault. The split is not
   * undone, for the reason `handleSplitAsync` gives for the crash case. A toast rather than an alert,
   * because the app shows toasts, so it outlives this page closing. It has no timer: the user needs
   * time to read what to do next.
   */
  private showSplitTuneNoSolutionToast(reason: CannotTuneReason) {
    this.toastCtrl.showToast({
      message: reason === CannotTuneReason.UntunableRows
        ? LL.editIngredient.splitTuneNoSolutionLockedToast()
        : LL.editIngredient.splitTuneNoSolutionToast(),
      type: ToastType.WarningLight,
      showCloseButton: true,
    });
  }

  /**
   * Tell the user that the day they picked holds no meal, so the food has nowhere to
   * go. Both save paths below can hit that state, which is why the wording lives in
   * one place rather than being written out twice.
   */
  private async showNoMealOnDayAlertAsync() {
    // if the page is covered or gone, say nothing. The food-read failure alerts below
    // also check `!this.auth.isLikelySignOutRace()`; that is absent here on purpose,
    // because it separates a read that failed during sign-out from one that really
    // failed, and this is not a failed read at all.
    if (this.viewDestroyed) return;

    await this.alertController.showAlertAsync<void>({
      title: LL.errors.userFacing.noMealOnDayTitle(),
      message: LL.errors.userFacing.noMealOnDay(),
      buttons: [
        {
          text: LL.common.buttons.ok(),
          role: AlertButtonRole.Primary,
        },
      ],
    });
  }

  /**
   * Tell the user a food could not be read, unless the page has gone or the whole app is in
   * the middle of signing out. Both save paths below show this same alert after putting the
   * meal back, which is why the wording and its two conditions live in one place.
   */
  private async showFoodLoadFailedIfStillVisibleAsync() {
    if (this.viewDestroyed || this.auth.isLikelySignOutRace()) return;

    await showFoodLoadFailedAlertAsync(this.alertController);
  }

  /**
   * Add ingredients to a meal, work out its new nutrients, and save it — putting the meal
   * back exactly as it was if a food could not be read.
   *
   * Both of this page's Log-mode paths end up doing this: `handleSave` logs the one food the
   * user is looking at (or every ingredient of a past meal being copied), and
   * `handleSplitAsync` logs the parts a recipe was split into. What they do AFTERWARDS is
   * deliberately different and stays with each caller — see `saveToMealAndNavigateAsync` and
   * `splitAndSaveToMealAsync` below. The middle is the same, and it is the part that must not
   * drift: the meal object is shared with whatever page is on screen, so a save that cannot go
   * ahead has to undo its own edit, or the app goes on showing ingredients that were never
   * stored.
   *
   * Telling the user is deliberately left to the caller, because the two differ: a caller that
   * gets `saved: false` must show `showFoodLoadFailedIfStillVisibleAsync` itself, in whatever
   * order suits it. The meal has already been put back by the time this returns; only telling
   * the user is outstanding.
   *
   * `previousIngredients` comes back on success because both logging paths need it to offer an
   * Undo once the page has closed. It is the meal's rows as they were BEFORE this method ran, so
   * it carries the amounts from before any tuning as well as from before the append -- which is
   * exactly what an Undo has to put back. `previousBasedOnFoodId` is the source marker from
   * before, for the same Undo (#6188).
   *
   * The split path may also hand over re-weighed copies of the meal's own rows -- see
   * `tunedExistingIngredients` below -- in which case this replaces the meal's rows with them as
   * well as appending. Every other caller only appends.
   */
  private async appendIngredientsAndSaveMealAsync(
    meal: Log_Meal,
    ingredientsToAppend: Ingredient[],
    options?: {
      basedOnFoodId?: string;
      /**
       * The meal's OWN rows, re-weighed by Split & Auto-Tune, to be written back over them as
       * the ingredients are appended (df-0a55eaa0a611). Only the split path passes it, and only
       * when the tuner actually ran: the parts being appended were sized on the assumption that
       * these rows would move too, so appending the parts alone saves a meal that does not add
       * up to the figure it was tuned to.
       *
       * They are applied only while they still describe the meal's current rows -- see
       * `tunedRowsStillMatchMeal`, which says why that can stop being true. When they do not,
       * the meal's own rows are kept and only the parts are appended, which is what this method
       * did before.
       */
      tunedExistingIngredients?: Ingredient[];
    },
  ): Promise<{ saved: true; previousIngredients: Ingredient[]; previousBasedOnFoodId: string | undefined } | { saved: false }> {
    // remember what the meal looked like first, so the lines below can be undone if the save
    // cannot go ahead. this is the ORIGINAL array, untouched by the tuning below, because the
    // tuned rows are copies -- so putting it back restores the old amounts as well as the old rows.
    const previousIngredients = meal.ingredients;
    const previousBasedOnFoodId = meal.basedOnFoodId;

    const tunedExistingIngredients = options?.tunedExistingIngredients;
    const applyTunedRows = tunedExistingIngredients !== undefined
      && tunedRowsStillMatchMeal(previousIngredients, tunedExistingIngredients);
    const rowsToKeep = applyTunedRows ? tunedExistingIngredients : previousIngredients;

    // the tuner answered about rows this meal no longer holds. writing its amounts over whatever
    // is there now would put one food's amount on another food, so keep what the meal has and
    // append the parts only -- and report it, because a Split & Auto-Tune that silently half
    // applied is exactly what df-0a55eaa0a611 was about.
    if (tunedExistingIngredients && !applyTunedRows) {
      this.logging.trackSilentProblem('split-tune: the meal changed while it was being tuned', {
        mealId: meal._id,
        mealIngredientCount: previousIngredients.length,
        tunedIngredientCount: tunedExistingIngredients.length,
      });
    }

    meal.ingredients = rowsToKeep.concat(ingredientsToAppend);

    // record the recipe or past meal these ingredients came from, when there was one and the meal
    // held nothing else: a meal that already had food is not "based on" what was added to it (#6188)
    const marker = markerAfterAdding(previousIngredients.length === 0, previousBasedOnFoodId, options?.basedOnFoodId);
    if (marker !== undefined) {
      meal.basedOnFoodId = marker;
    }

    // recalculate meal nutrients. A food that could not be READ makes this throw instead of
    // quietly leaving that ingredient out of the total, so put the meal back as it was and let
    // the caller tell the user it is a connection problem. Saving here would store a total we
    // know is too low.
    try {
      await this.foodDb.recalculateMealNutrientsAsync(meal);
    } catch (e) {
      if (e instanceof FoodReadFailedError) {
        meal.ingredients = previousIngredients;
        meal.basedOnFoodId = previousBasedOnFoodId;
        return { saved: false };
      }
      throw e;
    }

    // save the meal
    await this.userDb.saveEntriesAsync(DatabaseWrapper.getTransactionId(), [meal]);

    // notify subscribers that the day was updated
    this.pubSub.publish(DayUpdatedEvent, {});

    return { saved: true, previousIngredients, previousBasedOnFoodId };
  }

  private async splitAndSaveToMealAsync(
    subIngredients: Ingredient[],
    basedOnFoodId?: string,
    options?: {
      /** Runs once the meal has saved, and only then -- for a message that must not claim a split
       * that never landed. */
      onSaved?: () => void;
      /** The meal's own rows as Split & Auto-Tune re-weighed them, when it ran (df-0a55eaa0a611).
       * Saved together with the parts, because the two halves were sized as one set. */
      tunedExistingIngredients?: Ingredient[];
    },
  ) {
    const meal = this.selectedLogMeal;
    if (!meal) {
      await this.showNoMealOnDayAlertAsync();
      return;
    }

    const result = await this.appendIngredientsAndSaveMealAsync(meal, subIngredients, {
      basedOnFoodId,
      tunedExistingIngredients: options?.tunedExistingIngredients,
    });
    if (!result.saved) {
      // the meal has been put back already, so all that is left is to tell the user.
      await this.showFoodLoadFailedIfStillVisibleAsync();
      return;
    }

    // the page's own copy of the meal's rows was the list the tuning started from, and the save
    // may have replaced it. point it at what was actually stored, so any total this page works
    // out before it navigates away is the saved meal's, not the list it no longer holds.
    this.ingredients = meal.ingredients;

    // the split is saved, so a message about it is now true
    options?.onSaved?.();

    // say how many foods went in, and offer to take them back, as a split on the meal screen
    // does (#6188). The page still moves on to the meal below; the toast is the app's, so it
    // outlives this page.
    const undo = this.makeUndoLoggedFood(meal._id, result.previousIngredients, meal.ingredients, result.previousBasedOnFoodId);
    this.toastCtrl.showToast({
      message: LL.ingredientSearch.repeatMeal.addedToast({ count: subIngredients.length, meal: this.mealDisplayName(meal) }),
      actionButtonText: LL.common.buttons.undo(),
      onAction: () => { void undo(); },
      ...FOOD_ADDED_TOAST_PRESENTATION,
    });

    // check if NutritionPage is on the current navigation stack
    let useNutritionPageAsRoot = false;
    for (let i = 0; i < this.navCtrl.length(); i++) {
      const page = this.navCtrl.getByIndex(i);
      const pageName = getPageName(page.component.componentType);
      if (pageName === 'NutritionPage') {
        useNutritionPageAsRoot = true;
        break;
      }
    }

    // navigate to EditMealPage
    // use NutritionPage as root if it's on stack, otherwise use HomePage
    const rootPage = useNutritionPageAsRoot
      ? { page: 'NutritionPage', params: { day: this.dayDate } }
      : { page: 'HomePage', params: {} };

    await this.navCtrl.setPagesAsync([
      rootPage,
      { page: 'EditMealPage', params: { id: meal._id } }
    ], { updateUrl: true });
  }

  async handleDelete() {
    if (this.isExiting) return;
    let confirm = await this.alertController.showAlertAsync<boolean>({
      title: LL.ingredientSearch.alerts.removeTitle(),
      message: LL.ingredientSearch.alerts.removeMessage({ name: this.ingredient.name }),
      buttons: [
        {
          text: LL.common.buttons.cancel(),
          role: AlertButtonRole.Cancel,
          result: false,
        },
        {
          text: LL.common.buttons.remove(),
          role: AlertButtonRole.Primary,
          result: true,
        },
      ],
    });

    if (!confirm) return;

    this.isExiting = true;
    this.leaveWithoutAsking.set();
    const result: EditIngredientResult = { action: 'delete' };
    await this.navCtrl.popAsync(result);
  }

  /**
   * Runs before the page closes, whichever way the user leaves: the back arrow,
   * the browser back button, Android's hardware back button or a swipe back.
   * Without it, the amount and measure the user had just set were thrown away in
   * silence (#5058).
   *
   * The page edits a deep copy and hands it back to its caller on accept, so
   * there is nothing to save it into from here — the only real question is
   * whether the user meant to drop the change.
   */
  async ionViewCanLeave(): Promise<boolean> {
    // every path that closes the page itself arms this first — accept, cancel,
    // remove, and both split paths — so the guard does not ask a second time. It
    // is armed alongside `isExiting` rather than reusing it, because reading this
    // clears it and `isExiting` must stay set.
    if (this.leaveWithoutAsking.consume()) return true;
    if (!this.hasUnsavedIngredientEdits()) return true;
    return confirmDiscardAsync(this.alertController, {
      title: LL.unsavedChanges.discard.ingredientTitle(),
      message: LL.unsavedChanges.discard.ingredientMessage(),
    });
  }

  /** Whether the ingredient on screen differs from the one the page opened with. */
  private hasUnsavedIngredientEdits(): boolean {
    if (!this.ingredient || !this.ingredientAsLoaded) return false;
    return !deepCompareEQ(this.ingredient, this.ingredientAsLoaded);
  }

  async handleViewDetails() {
    if (this.sourceLogMeal) {
      // for log meal previews, open the source meal page (inline PageRef to avoid circular import)
      await this.navCtrl.pushAsync({ pageName: 'EditMealPage' } as PageRef<any, any>, {
        id: this.sourceLogMeal._id,
      });
      return;
    }
    if (isIngredientReal(this.ingredient)) {
      await this.navCtrl.pushAsync(IEditFoodPage, {
        id: this.ingredient.foodId,
        mode: 'view',
        logReturnsToCaller: true,
        returnCallback: 'onFoodEdited',
      });
    }
  }

  /**
   * Called via returnCallback when EditFoodPage is closed.
   * Updates the ingredient's foodId if the food was edited and a new copy was created.
   */
  async onFoodEdited(_context: unknown, result: string | FoodDetailsResult) {
    // if page was closed without saving (e.g. back button), ignore
    if (!result) return;
    // if the user pressed Log there, there is nothing to do: this is already the portion screen
    // for that food, still holding the meal or day it was opened for (#5520)
    if (typeof result !== 'string') return;
    const newFoodId = result;

    // if food ID changed (new private copy created from official food)
    if (isIngredientReal(this.ingredient) && newFoodId !== this.ingredient.foodId) {
      this.ingredient.foodId = newFoodId;

      // reload the food data with the new ID
      await this.loadIngredientDataAsync();
      await this.updateDisplayNutrientsAsync();
      this.cd.markForCheck();
    }
  }

  async handleSave() {
    if (this.isExiting) return;
    // if the amount cannot be used, do nothing: the button is disabled then, but the on-screen
    // keypad can press it directly while the field is still being edited (#4967). Checked
    // before anything is cached, so the food's remembered amount is not overwritten either.
    if (this.hasUnusableAmount) return;
    this.isExiting = true;
    // NOT arming `leaveWithoutAsking` here. Several paths below keep the page
    // ALIVE — a food that could not be read puts the meal back, clears
    // `isExiting` and invites a retry — and a flag armed here would stay armed, so
    // the next Back would discard the amount in silence. It is armed immediately
    // before each way out instead.
    // update cached kcal
    if (isIngredientReal(this.ingredient) && this.food) {
      // the same reference weight the screen above divided by, and the same one
      // recalculateMealNutrientsAsync uses when it works the meal total out again. This used to
      // take the food's own stored total weight and fall back to 100, which is the RECIPE rule
      // applied to every food: a record carrying a non-zero total weight while not being a
      // recipe -- an old or damaged one whose id says recipe but whose ingredient list is
      // missing -- got its cached calories divided by that weight while everything else on
      // screen, and the meal total written to the log, divided by 100. See df-6c3a21e8bfd3.
      const refWeight = getFoodReferenceWeight(this.food);
      this.ingredient.kcal = (this.food.nutrients[INFOODS.Calories] * this.ingredient.weight_g) / refWeight;

      // cache last used measure
      this.food.local = this.food.local || {};
      this.food.local.lastUsedMeasureUnitName = this.ingredient.custom_unit;
      this.food.local.lastUserMeasureWeight_g = this.ingredient.weight_g;
      this.food.local.lastUsedWeightUnit = this.weight_unit || undefined;
      // remember the "Weighed with peel" tick for next time, only where the box was offered
      if (this.weighedWithLabel !== undefined) {
        this.food.local.lastWeighedWithRefuse = this.isWeighedWithRefuseTicked;
      }
      await this.userDb.cacheFoodAsync(this.food);
    } else if (isIngredientMacro(this.ingredient)) {
      this.ingredient.kcal = (this.ingredient.nutrients[INFOODS.Calories] * this.ingredient.weight_g) / 100;
    }

    // Log mode: save ingredient to meal and navigate to meal page
    if (this.mode === EditIngredientMode.Log) {
      const refusal = await this.saveToMealAndNavigateAsync();
      // if the page navigated away there is nothing left to say and nothing to unwind
      if (!refusal) return;

      // the save did not go ahead, so this page is still the one on screen. clear the flag
      // this method set -- leaving it set would make Log, Cancel and Remove all do nothing
      // for the rest of the visit, so the alert below would invite a retry the page can no
      // longer perform. cleared BEFORE the alert is waited on, so the page is usable the
      // moment the user dismisses it.
      this.isExiting = false;
      switch (refusal) {
        case SaveToMealRefusal.NoMealOnDay:
          await this.showNoMealOnDayAlertAsync();
          break;
        case SaveToMealRefusal.FoodCouldNotBeRead:
          await this.showFoodLoadFailedIfStillVisibleAsync();
          break;
        case SaveToMealRefusal.PastMealHasNoUsableFoods:
          // the loader skips meals with no foods, so getting here means the stored meal is in a
          // shape this version cannot read -- the same report and wording Add Again uses
          this.logging.trackSilentProblem('RepeatMealHadNoUsableIngredients', { mealId: this.sourceLogMeal?._id });
          this.toastCtrl.showToast({
            message: LL.ingredientSearch.repeatMeal.couldNotRepeatToast(),
            type: ToastType.WarningLight,
            duration: FOOD_ADDED_TOAST_DURATION_MS,
          });
          break;
        default: {
          // a refusal nobody wrote an answer for. the `never` makes this a compile error the
          // moment a member is added, which is the point -- a silent `else` arm sending a new
          // refusal to the wrong alert is the shape this whole refactor exists to remove.
          const unhandled: never = refusal;
          this.logging.trackSilentProblem('edit-ingredient: unhandled save refusal', {
            refusal: String(unhandled),
          });
          await this.showFoodLoadFailedIfStillVisibleAsync();
        }
      }
      return;
    }

    // if confirming a recent meal, return its ingredients as split result
    if (this.sourceLogMeal) {
      try {
        const { ingredients, basedOnFoodId } = await this.getSplitIngredientsAsync();
        const result: EditIngredientResult = {
          action: 'split',
          splitIngredients: ingredients,
          basedOnFoodId,
        };
        this.leaveWithoutAsking.set();
        await this.navCtrl.popAsync(result);
      } catch (e) {
        // same as handleSplit above: a bare console.error here reached neither Sentry nor the
        // user, so a Log press that failed looked like a button that does nothing. what can
        // realistically throw is handing the foods back to the meal, not reading them, so
        // the split message (which blames a missing ingredient) would mislead here.
        this.logging.trackAbnormalException(e, LL.errors.userFacing.confirmRecentMealFailed());
        this.isExiting = false;
      }
      return;
    }

    const result: EditIngredientResult = {
      action: 'save',
      ingredient: this.ingredient,
      // worked out here because this is the only place that holds the food's own measures
      // and the unit the user was looking at (#4967)
      loggedAmountDescription: this.describeLoggedAmount(),
    };
    this.leaveWithoutAsking.set();
    await this.navCtrl.popAsync(result);
  }

  /**
   * Log the food into the day's meal and leave the page.
   *
   * Returns nothing when it navigated away, and a `SaveToMealRefusal` when the save could not
   * go ahead and this page is therefore still on screen. Saying which of the two happened is
   * all this method does about it: the `isExiting` flag belongs to `handleSave`, which sets it,
   * and so does telling the user -- `handleSave` clears the flag and then shows the alert that
   * matches the answer. Keeping both with the one method that sets the flag is what stops a
   * future early return here from having to work out whether its caller armed anything
   * (df-998ad478267d, and the defect that came of getting it wrong, df-3e2ce2631f82).
   */
  private async saveToMealAndNavigateAsync(): Promise<SaveToMealRefusal | undefined> {
    const meal = this.selectedLogMeal;
    if (!meal) {
      return SaveToMealRefusal.NoMealOnDay;
    }

    // if copying from a log meal, add copies of all its ingredients, never the stored meal's own
    // objects (#6188: the meal screen edits rows in place, which would change the earlier meal
    // too), together with its source marker, which an empty meal takes on.
    // otherwise add the single ingredient
    const ingredientsToAdd = this.sourceLogMeal
      ? ingredientsToRepeat(this.sourceLogMeal)
      : [this.ingredient];
    // a past meal written by an older version may hold nothing loggable. Adding nothing would
    // still record it as the empty meal's source and announce "0 foods added", so refuse, as
    // Add Again does for the same meal.
    if (ingredientsToAdd.length === 0) {
      return SaveToMealRefusal.PastMealHasNoUsableFoods;
    }

    const result = await this.appendIngredientsAndSaveMealAsync(meal, ingredientsToAdd, {
      basedOnFoodId: this.sourceLogMeal?.basedOnFoodId,
    });
    if (!result.saved) {
      // the meal has been put back already, so all that is left is for the caller to say so.
      return SaveToMealRefusal.FoodCouldNotBeRead;
    }
    const previousIngredients = result.previousIngredients;

    // "Food Logged" (#4967). This route — food list, portion screen, Log — had no telemetry of
    // its own: the only emitter of this event used to be the food list's per-card "+" button,
    // which this ticket removed, so the metric went silently to zero. It belongs here rather
    // than on the button, because this is the point the food is actually written to the day.
    this.telemetry.trackEvent('foods_log_food', {});

    // Say what happened, and offer to take it back. Read the wording out BEFORE leaving:
    // once the page is gone, `this.ingredient` is still ours (the page edits a deep copy) but
    // the toast is shown by the app, not by this page, so everything it needs is captured
    // here as plain values.
    const mealName = this.mealDisplayName(meal);
    // a past meal says how many foods went in, as it does on the meal screen (#6188). Worded as
    // one food, it read "Meal 4 523 g added to Meal 1".
    const message = this.sourceLogMeal
      ? LL.ingredientSearch.repeatMeal.addedToast({ count: ingredientsToAdd.length, meal: mealName })
      : buildFoodAddedToastMessage({
        foodName: this.ingredient.name,
        amountDescription: this.describeLoggedAmount(),
        mealName,
      });
    const undo = this.makeUndoLoggedFood(meal._id, previousIngredients, meal.ingredients, result.previousBasedOnFoodId);

    // Raised BEFORE the page closes, not after. The pop takes a 500ms slide transition and its
    // promise resolves only once that has finished, so showing the toast on the far side of it
    // made the confirmation arrive half a second after the press that earned it. Everything the
    // toast needs was read out above, so there is nothing left here that the page has to stay
    // alive for.
    this.toastCtrl.showToast({
      message,
      actionButtonText: LL.common.buttons.undo(),
      onAction: () => { void undo(); },
      ...FOOD_ADDED_TOAST_PRESENTATION,
    });

    // Back to the list the user came from, rather than on to the meal page (#4967). Logging
    // one food used to REPLACE the whole navigation stack with Nutrition → Edit Meal, which
    // took the user somewhere they had not asked to go and made logging a second food a
    // journey. Leaving the way we came in means Log and the header's back arrow agree.
    this.leaveWithoutAsking.set();
    await this.navCtrl.popAsync();
    // the page is gone, so there is no refusal to report and nothing for the caller to undo
    return undefined;
  }

  /**
   * How much was logged, worded the way the food lists word it — "2 large" for a food that
   * defines its own measures, "100 g" otherwise. `formatServingSize` is the same helper the
   * food cards use, so the toast and the list cannot drift apart.
   */
  private describeLoggedAmount(): string {
    const ingredient = this.ingredient;
    const weight_g = ingredient.weight_g;
    const customUnitName = isIngredientReal(ingredient) ? ingredient.custom_unit : undefined;
    if (customUnitName) {
      // the custom measure's own amount: how many of them this weight comes to. Same
      // arithmetic as `getIngredientServingAmount` in ingredient-list.ts. `weight_g` is left
      // out so the toast reads "2 large" rather than "2 large | 100 g" — the toast is a
      // confirmation, not a data row.
      const unit = this.customIngredientUnits.find(u => u.id === customUnitName);
      if (unit?.value1) {
        return formatServingSize(
          { name: customUnitName, amount: weight_g / unit.value1, weight_g: undefined },
          this.settings.locale,
        );
      }
    }
    return formatServingSize(
      { name: undefined, amount: undefined, weight_g },
      this.settings.locale,
      { weightUnit: this.weight_unit || 'g' },
    );
  }

  /**
   * Build the "Undo" the toast offers after a food is logged (#4967).
   *
   * It runs AFTER this page has been closed, so it deliberately touches only app-wide
   * services and the plain values captured here — no view work, no change detection.
   *
   * The meal is re-read rather than trusted, because time passes between logging and
   * pressing Undo: another device can sync, or the user can open the meal and edit it. Two
   * outcomes are safe to act on, and everything else is reported rather than passed over in
   * silence:
   *
   * - the meal still holds exactly what we wrote → put back what it held before. This is the
   *   right answer for copying a whole past meal too, where several ingredients arrived at
   *   once.
   * - it has changed, but the entries we appended are still there at the end → take just
   *   those off, leaving whatever else happened meanwhile alone.
   *
   * Either way the meal's source marker goes back to `previousBasedOnFoodId`, removing the
   * field when the meal had none (#6188): a past meal or recipe logged into an empty meal sets
   * it, and Undo must not leave the meal "based on" foods it no longer holds.
   */
  private makeUndoLoggedFood(
    mealId: string,
    previousIngredients: Ingredient[],
    writtenIngredients: Ingredient[],
    previousBasedOnFoodId: string | undefined,
  ): () => Promise<void> {
    // deep copies: the arrays above keep being used by whatever page is on screen now
    const before = deepClone(previousIngredients);
    const written = deepClone(writtenIngredients);
    const appendedCount = written.length - before.length;

    return async () => {
      try {
        const meal = await this.userDb.backlogCache.getEntryByIdAsync(mealId) as Log_Meal;
        if (!meal || meal.type !== LogRecordType.Meal) {
          this.reportUndoFailed('LoggedFoodUndoMealMissing', { mealId });
          return;
        }

        const current = meal.ingredients || [];
        if (deepCompareEQ(current, written)) {
          meal.ingredients = before;
        } else if (appendedCount > 0
          && current.length >= appendedCount
          && deepCompareEQ(current.slice(current.length - appendedCount), written.slice(before.length))) {
          meal.ingredients = current.slice(0, current.length - appendedCount);
        } else {
          // the meal moved on in a way we cannot unpick. Removing anything here could delete
          // food the user logged since, so do nothing to the data and say so.
          this.reportUndoFailed('LoggedFoodUndoMealChanged', {
            mealId,
            currentCount: current.length,
            expectedCount: written.length,
          });
          return;
        }
        if (previousBasedOnFoodId === undefined) {
          delete meal.basedOnFoodId;
        } else {
          meal.basedOnFoodId = previousBasedOnFoodId;
        }

        await this.foodDb.recalculateMealNutrientsAsync(meal);
        await this.userDb.saveEntriesAsync(DatabaseWrapper.getTransactionId(), [meal]);
        this.pubSub.publish(DayUpdatedEvent, {});

        // Counted only when the food really came back out, not when Undo was merely pressed —
        // a press that could not be carried out reports through `reportUndoFailed` instead, so
        // the two are never confused in the numbers.
        this.telemetry.trackEvent('food_added_undo', {});
      } catch (e) {
        this.reportUndoFailed('LoggedFoodUndoFailed', { error: describeErrorForLog(e) });
      }
    };
  }

  /** Undo could not be carried out: tell the user, and file it so it is not lost. */
  private reportUndoFailed(name: string, context: Record<string, unknown>) {
    this.logging.trackSilentProblem(name, context);
    this.toastCtrl.showToast({
      message: LL.editIngredient.undoFailedToast(),
      type: ToastType.WarningLight,
      duration: FOOD_ADDED_TOAST_DURATION_MS,
    });
  }

  mapNutrientUnitIdToMeasurementType(unitId: string): string {
    switch (unitId) {
      case 'g':
        return 'nutrient_g';
      case 'mg':
        return 'nutrient_mg';
      case 'ug':
        return 'nutrient_ug';
      case 'kcal':
        return 'nutrient_kcal';
      default:
        return 'integer';
    }
  }

  trackByNutrientTag(_index: number, displayNutrient: UiDisplayNutrient): string {
    return displayNutrient.tag;
  }
}
